// @ts-check

import {
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeFilled,
  InfoCircleOutlined,
  Loading3QuartersOutlined,
  MinusCircleFilled,
  MoreOutlined,
  ReloadOutlined,
  SettingFilled,
  ThunderboltOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import {
  Alert,
  Avatar,
  Button,
  Card,
  Col,
  DatePicker,
  Dropdown,
  Empty,
  Image,
  InputNumber,
  List,
  Menu,
  Modal,
  notification,
  PageHeader,
  Popover,
  Radio,
  Row,
  Select,
  Space,
  Spin,
  Switch,
  Tag,
  Tooltip,
  Transfer,
  Typography,
} from "antd";
import useModal from "antd/lib/modal/useModal";
import axios from "axios";
import dayjs from "dayjs";
import moment from "moment";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { BiDevices, BiNoSignal, BiSignal1, BiSignal2, BiSignal3, BiSignal4, BiSignal5 } from "react-icons/bi";
import { BsBatteryHalf } from "react-icons/bs";
import { CiWarning } from "react-icons/ci";
import { FaBatteryEmpty, FaBatteryFull, FaBatteryHalf, FaBatteryQuarter, FaBatteryThreeQuarters, FaChartBar, FaPlug } from "react-icons/fa";
import { GiPlantWatering } from "react-icons/gi";
import { GrDrag } from "react-icons/gr";
import { IoBluetooth, IoCloud, IoCloudDoneOutline, IoCloudOfflineOutline, IoMapOutline } from "react-icons/io5";
import { MapContainer, useMap } from "react-leaflet";
import { useMutation, useQueries, useQuery, useQueryClient } from "react-query";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import BL from "../assets/device-BL-128x128.png";
import EL from "../assets/device-EL-128x128.png";
import ELS from "../assets/device-ELS-128x128.png";
import LT from "../assets/device-LT-128x128.png";
import NEM from "../assets/device-NEM-128x128.png";
import { OptimizationProvider } from "../contexts/optimization-context";
import tr from "../i18n";
import { formatUtcWithOffset } from "../utils/DateFormatter";
import { fromLitres, getNormalizedBatteryPct, toLitres } from "../utils/DeviceUtils";
import useWindowSize from "../utils/WindowSize";
import { useGarden } from "./garden-context";
import { MapsProvider } from "./maps-context";
import { useAplanStream, useLiveRemaining, fmt } from "../hooks/useAplanStream"

const GALLON_TO_LITRE = 3.78541;
const DEFAULT_LIMIT_LITRES = 1000;
const DEFAULT_LIMIT_GALLONS = 250;

/** @type {import("../types/global").BatteryManagementModes} */
export const BATTERY_MANAGEMENT_MODES = {
  BATTERY_SAVER: { edrx_mode: "edrx-80", edrx: "80", id: "battery_saver", mgmt: "psm", period: "21600" },
  NORMAL: { edrx_mode: "edrx-40", edrx: "40", id: "normal", mgmt: "psm", period: "1800" },
  REAL_TIME: { edrx_mode: "edrx-20", edrx: "20", id: "real_time", mgmt: "normal", period: "" },
};

export const deviceImageMap = {
  BL: BL,
  ELS: ELS,
  EL: EL,
  LT: LT,
  NEM: NEM,
};

const normalizeTickValue = (value) => {
  if (typeof value !== "number") return value;
  return Number(value.toFixed(3));
};

/**
 * Get icon for battery mode
 * @param {keyof import("../types/global").BatteryManagementModes} key
 * @returns {import("react").JSX.Element | null}
 */
function getBatteryModeIcon(key) {
  switch (key) {
    case "BATTERY_SAVER":
      return <FaBatteryQuarter style={{ fontSize: 20, color: "#52c41a" }} />;
    case "NORMAL":
      return <FaBatteryThreeQuarters style={{ fontSize: 20, color: "#1890ff" }} />;
    case "REAL_TIME":
      return <ThunderboltOutlined style={{ fontSize: 20, color: "#faad14" }} />;
    default:
      return null;
  }
}

/**
 * @param {import("../types/global").Device["mode"]} deviceMode
 * @param {import("../types/global").Device["period"]} [period]
 * @param {import("../types/global").Device["firmwareVersion"]} [version] *
 * @returns {import("../types/global").Prettify<import("../types/global").BatteryManagementMode["id"]> | (string & {})}
 */
function isDeviceOnMode(deviceMode, period, version) {
  if (!deviceMode) return "";

  if (version?.startsWith("SPB_") || version?.startsWith("SPBUS_") || version?.startsWith("KRM_")) {
    if (deviceMode === BATTERY_MANAGEMENT_MODES.BATTERY_SAVER.edrx_mode) return "battery_saver";
    if (deviceMode === BATTERY_MANAGEMENT_MODES.NORMAL.edrx_mode) return "normal";
    if (deviceMode === BATTERY_MANAGEMENT_MODES.REAL_TIME.edrx_mode) return "real_time";
    if (deviceMode === "normal") return "real_time";
  } else {
    if (deviceMode === BATTERY_MANAGEMENT_MODES.BATTERY_SAVER.mgmt && period === BATTERY_MANAGEMENT_MODES.BATTERY_SAVER.period) return "battery_saver";
    if (deviceMode === BATTERY_MANAGEMENT_MODES.NORMAL.mgmt && period === BATTERY_MANAGEMENT_MODES.NORMAL.period) return "normal";
    if (deviceMode === BATTERY_MANAGEMENT_MODES.REAL_TIME.mgmt) return "real_time";
  }

  return "";
}

const DeviceContext = /** @type {import("react").Context<import("../types/device").DeviceContextValue | undefined>} */ (createContext(undefined));

export function useDevice() {
  const context = useContext(DeviceContext);
  if (!context) throw new Error("useDevice must be used within a DeviceProvider");
  return context;
}


/**
 * @param {import("react").PropsWithChildren<{}>} props
 * @returns {import("react").JSX.Element | null}
 */
export function DeviceProvider({ children }) {
  const { id: gardenId } = useParams();
  const { search } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams(search);
  // State derived from URL (single source of truth)
  const page = parseInt(searchParams.get("page") || "1");
  const limit = parseInt(searchParams.get("limit") || "100");
  const searchTerm = searchParams.get("search") || "";
  const model = searchParams.getAll("model")?.join(",") || undefined;
  const alive = searchParams.get("alive") || undefined;
  const sortField = searchParams.get("sortField") || undefined;
  const sortOrder = searchParams.get("sortOrder") || undefined;
  const gardenIds = searchParams.getAll("gardenIds")?.join(",") || undefined;

  const deviceSerialParam = searchParams.get("deviceSerial");

  // We might still need garden info for some operations, but we should be careful if it's not available
  // If we are in a garden route, useGarden should work. If not, it might throw or return undefined.
  // Since DeviceProvider is used inside GardenProvider in some routes, we can try to use it safely.
  // But for "All Devices" route, GardenProvider is NOT present.
  // So we cannot rely on useGarden() here unconditionally.
  // We will rely on gardenId from useParams.

  const [isDraggingEnabled, setIsDraggingEnabled] = useState(false);
  const [modal, contextHolder] = useModal();
  const queryClient = useQueryClient();
  const [device_id, setDeviceId] = useState(""); // For dashboard
  const [end_date, setEndDate] = useState(moment().add(1, "day").endOf("day"));
  const [start_date, setStartDate] = useState(moment().subtract(7, "day").startOf("day"));
  /** @type {import("react").MutableRefObject<ReturnType<import("antd/lib/modal/confirm").ModalFunc> | null>} */
  const modalRef = useRef(null);
  const { width: windowWidth } = useWindowSize();
  const isListPage = window.location.pathname.includes("/devices");
  const effectiveLimit = isListPage ? limit : 10000; // or something large
  const queryState = useMemo(() => ({
    page,
    limit: effectiveLimit,
    searchTerm,
    model,
    alive,
    sortField,
    sortOrder,
    gardenIds
  }), [page, effectiveLimit, searchTerm, model, alive, sortField, sortOrder, gardenIds]);

  // --- Queries ---

  // 1. Fetch Devices (Garden or All)
  const {
    data: devicesResponse,
    isFetching: isFetchingDevicesResponse,
    refetch: refetchDevices,
  } = useQuery(
    ["Devices", gardenId, isListPage ? "list" : "summary", page, effectiveLimit, searchTerm, model, alive, sortField, sortOrder, gardenIds],
    () => {
      const url = gardenId
        ? `${process.env.REACT_APP_PLANNER_API_END_POINT}/garden/${gardenId}/device`
        : `${process.env.REACT_APP_PLANNER_API_END_POINT}/garden/device/all`;

      return axios.get(url, {
        params: {
          page,
          limit: effectiveLimit,
          search: searchTerm || undefined,
          model,
          alive,
          sortField,
          sortOrder,
          gardenIds,
        },
      });
    },
    {
      keepPreviousData: false,
      refetchOnWindowFocus: false,
    }
  );

  const devices = useMemo(() => devicesResponse?.data?.devices || [], [devicesResponse]);
  const totalDevices = useMemo(() => devicesResponse?.data?.pagination?.total || 0, [devicesResponse]);

  const deviceInternalId = useMemo(() => {
    const deviceID = searchParams.get("device_id");
    if (!deviceID) return null;

    return devices.find((device) => device.id === deviceID)?._id ?? null;
  }, [searchParams, devices]);

  const devicesByModel = useMemo(() => {
    const _devicesByModel = devices?.reduce((acc, device) => {
      const { model } = device;

      if (!model) return acc;

      if (!acc[model]) acc[model] = /** @type {import("../types/global").Device[]} */ ([]);

      acc[model].push(device);

      return acc;
    }, /** @type {{ [model in import("../types/global").Device["model"]]: import("../types/global").Device[] }} */({}));
    return _devicesByModel;
  }, [devices]);

  const updateQueryParams = useCallback(
    (updates) => {
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        Object.entries(updates).forEach(([key, value]) => {
          const isEmpty =
            value === undefined ||
            value === null ||
            value === "" ||
            (Array.isArray(value) && value.length === 0);
          if (isEmpty) {
            params.delete(key);
          } else {
            params.set(key, Array.isArray(value) ? value.join(",") : String(value));
          }
        });
        return params;
      });
    },
    [setSearchParams],
  );

  // 2. Fetch Single Device (if deviceSerial exists)
  const { data: singleDeviceResponse, isFetching: isFetchingSingleDevice } =
    /** @type {import("react-query").UseQueryResult<import("axios").AxiosResponse<{success: boolean, device: import("../types/global").Device}>, unknown>} */ (
      useQuery(["Device", deviceSerialParam], () => axios.get(`${process.env.REACT_APP_PLANNER_API_END_POINT}/device`, { params: { deviceSerial: deviceSerialParam } }), {
        enabled: !!deviceSerialParam,
        refetchOnWindowFocus: false,
      })
    );

  const singleDevice = singleDeviceResponse?.data?.device;
  const locale = localStorage.getItem("locale") || "en";
  const dashboardUrl = locale === "en" ? process.env.REACT_APP_DEVICE_WATERING_DASHBOARD_EN : process.env.REACT_APP_DEVICE_WATERING_DASHBOARD_TR;
  // 3. Dashboard
  // Dates are part of the query key so every date change produces a fresh request
  // with the current values (no stale queryFn closure / manual refetch needed).
  const startDateParam = start_date ? start_date.format("YYYY-MM-DD") : null;
  const endDateParam = end_date ? end_date.format("YYYY-MM-DD") : null;

  const {
    data: deviceDashboard,
    isFetching: isFetchingDeviceDashboard,
    refetch: refetchDeviceDashboard,
  } = /** @type {import("react-query").UseQueryResult<import("axios").AxiosResponse<import("../types/global").GardenDashboardResponse>>} */ (
      useQuery(
        ["DeviceDashboard", gardenId, device_id, startDateParam, endDateParam],
        () =>
          axios.post(`${process.env.REACT_APP_PLANNER_API_END_POINT}/garden/dashboard`, {
            id: dashboardUrl,
            params: {
              device_id: device_id,
              start_date: startDateParam,
              end_date: endDateParam,
            },
          }),
        {
          enabled: !!device_id && !!startDateParam && !!endDateParam,
          // Keep showing the previous report (and the date picker) while the new one loads
          keepPreviousData: true,
          refetchOnWindowFocus: false,
        },
      )
    );

  // 4. NemPro Dashboard
  const {
    data: nemProDashboard,
    isFetching: isFetchingNemProDashboard,
    refetch: refetchNemProDashboard,
  } = /** @type {import("react-query").UseQueryResult<import("axios").AxiosResponse<import("../types/global").GardenDashboardResponse>>} */ (
      useQuery(
        ["NemProDashboard", gardenId, device_id, startDateParam, endDateParam],
        () =>
          axios.post(`${process.env.REACT_APP_PLANNER_API_END_POINT}/garden/dashboard`, {
            id: process.env.REACT_APP_HUMIDITY_DASHBOARD,
            params: {
              deviceserial: device_id,
              startdate: startDateParam,
              enddate: endDateParam,
            },
          }),
        {
          enabled: !!device_id && !!startDateParam && !!endDateParam,
          keepPreviousData: true,
          refetchOnWindowFocus: false,
        },
      )
    );

  // 5. Available Devices (for adding to garden)
  const {
    data: availableDevicesResponse,
    refetch: refetchAvailableDevices,
    isLoading: isLoadingAvailableDevices,
  } = /** @type {import("react-query").UseQueryResult<import("axios").AxiosResponse<import("../types/global").DevicesApiResponse>, unknown>} */ (
      useQuery(["GardenDevicesAvailable", gardenId], () => axios.get(`${process.env.REACT_APP_PLANNER_API_END_POINT}/garden/${gardenId}/device/available`), {
        enabled: false,
        refetchOnWindowFocus: false,
      })
    );

  // 6. Device Garden
  /**
   * @type {import("react-query").UseQueryResult<
   *   import("../types/global").GetGardenByDeviceIdSuccessResponse,
   *   unknown
   * >}
   */
  const { data: deviceGardenResponse, isLoading: isLoadingDeviceGarden } = useQuery(
    ["GardenByDevice", deviceInternalId],
    () => axios.get(`${process.env.REACT_APP_PLANNER_API_END_POINT}/garden/device/${deviceInternalId}`).then((r) => r.data),
    {
      enabled: !!deviceInternalId,
    },
  );

  // 7. Garden Options
  const { data: gardenOptions = [] } = useQuery(
    ["GardenFilterOptions"],
    () =>
      axios
        .get(`${process.env.REACT_APP_PLANNER_API_END_POINT}/garden`, { params: { fields: "minimal" } })
        .then((r) => r.data.gardens),
    { enabled: isListPage, staleTime: 5 * 60 * 1000, refetchOnWindowFocus: false }
  );

  // --- Mutations ---

  const { mutate: mutateDevicesOrder, isLoading: isMutatingDevicesOrder } =
    /** @type {import("react-query").UseMutationResult<import("axios").AxiosResponse<any, any>, unknown, any, unknown>} */ (
      useMutation((deviceIds) => axios.post(`${process.env.REACT_APP_PLANNER_API_END_POINT}/garden/${gardenId}/devices-order`, { deviceIds }), {
        onSuccess: () => {
          refetchDevices();
        },
        onError: (error) => {
          console.error("Device reordering failed:", error);
        },
      })
    );

  const { mutate: mutateAddDevices, isLoading: isAddingDevices } =
    /** @type {import("react-query").UseMutationResult<import("axios").AxiosResponse<import("../types/global").AddDevicesResponse>, unknown, string[], unknown>} */ (
      useMutation(
        (deviceIds) =>
          axios.put(`${process.env.REACT_APP_PLANNER_API_END_POINT}/garden/device`, {
            gardenId,
            deviceIds,
          }),
        {
          onSuccess: () => {
            refetchDevices();
            refetchAvailableDevices();
          },
          onError: (error) => {
            notification.error({
              message: `Device addition failed`,
            });
            console.error("Device addition failed:", error);
          },
        },
      )
    );

  const { mutate: mutateRemoveDevices, isLoading: isRemovingDevices } =
    /** @type {import("react-query").UseMutationResult<import("axios").AxiosResponse<import("../types/global").RemoveDevicesResponse>, unknown, string[], unknown>} */ (
      useMutation(
        (deviceIds) =>
          axios.delete(`${process.env.REACT_APP_PLANNER_API_END_POINT}/garden/device`, {
            data: {
              gardenId,
              deviceIds,
            },
          }),
        {
          onSuccess: () => {
            refetchDevices();
            refetchAvailableDevices();
          },
          onError: (error) => {
            console.error("Device removal failed:", error);
          },
        },
      )
    );

  const { mutate: mutateGardenManagement, isLoading: isMutatingGardenManagement } =
    /** @type {import("react-query").UseMutationResult<import("axios").AxiosResponse<import("../types/global").GardenManagementModeResponse>, unknown, import("../types/global").GardenManagementModeRequest, unknown>} */ (
      useMutation((request) => axios.post(`${process.env.REACT_APP_PLANNER_API_END_POINT}/garden/mgmt`, request), {
        onError: (error) => {
          console.error("Garden management mode update failed:", error);
        },
        onSettled: async () => {
          await queryClient.invalidateQueries(["Garden", gardenId]);
        },
        onSuccess: () => {
          refetchDevices();
        },
      })
    );

  const { mutate: mutateDeviceManagement, isLoading: isMutatingDeviceManagement } =
    /** @type {import("react-query").UseMutationResult<import("axios").AxiosResponse<import("../types/global").DeviceManagementModeResponse>, unknown, import("../types/global").DeviceManagementModeRequest, unknown>} */ (
      useMutation(
        ({ deviceSid, mgmtMode }) =>
          axios.post(`${process.env.REACT_APP_PLANNER_API_END_POINT}/device/${deviceSid}/mgmt`, {
            mode: mgmtMode,
          }),
        {
          onError: (error) => {
            console.error("Device management mode update failed:", error);
          },
          onSettled: async () => {
            await queryClient.invalidateQueries(["Garden", gardenId]);
            refetchDevices();
          },
        },
      )
    );

  const { isLoading: isLoadingUpdateDeviceName, mutate: mutateUpdateDeviceName } = useMutation(
    /** @param {import("../types/global").UpdateDeviceNameRequest} params */
    ({ deviceId, deviceName }) => {
      return axios.put(`${process.env.REACT_APP_GARDENER_API_END_POINT}/device/rename/${deviceId}`, {
        name: deviceName,
      });
    },
    {
      onSettled: (data, vars, ctx) => {
        queryClient.refetchQueries(["Garden", gardenId]);
        queryClient.refetchQueries(["Devices", gardenId]);
      },
    },
  );

  const { isLoading: isDeletingDevice, mutate: mutateDeleteDevice } = useMutation(
    /** @param {import("../types/global").DeleteDeviceRequest} params */
    ({ deviceId }) => {
      return axios.delete(`${process.env.REACT_APP_GARDENER_API_END_POINT}/device/delete/${deviceId}`);
    },
    {
      onSuccess: (data, vars, ctx) => {
        queryClient.refetchQueries(["Garden", gardenId]);
        queryClient.refetchQueries(["Devices", gardenId]);
        if (data.data.success === true) {
          notification.success({
            message: tr("Device deleted"),
            style: {
              zIndex: 9999,
            },
          });
        }
        else {
          notification.error({
            message: tr("Device deletion failed"),
            style: {
              zIndex: 9999,
            },
          });
        }
      },
    },
  );

  const { isLoading: isLoadingFullImage, mutateAsync: getFullValveImage } = useMutation(
    /** @param {import("../types/global").GetValveImageRequest} params */
    ({ deviceId, valveId }) => {
      return axios.get(`${process.env.REACT_APP_GARDENER_API_END_POINT}/device/${deviceId}/valve/${valveId}/image`, {
        responseType: "blob",
      });
    },
  );

  const { isLoading: isLoadingBulkValveImages, mutateAsync: getBulkValveImages } = useMutation(
    /** @param {import("../types/global").GetBulkValveImagesRequest} params */
    ({ deviceId, valveId }) => {
      return axios.get(`${process.env.REACT_APP_GARDENER_API_END_POINT}/device/${deviceId}/${valveId}/images`);
    },
  );

  const { isLoading: isChangingValveState, mutate: mutateChangeValveState } = useMutation(
    /** @param {import("../types/global").ChangeValveStateRequest} params */
    ({ deviceId, valveId, state }) => {
      return axios.put(`${process.env.REACT_APP_GARDENER_API_END_POINT}/device/${deviceId}/valve/${valveId}/state`, {
        state,
      });
    },
    {
      onSettled: (data, vars, ctx) => {
        queryClient.refetchQueries(["Garden", gardenId]);
        queryClient.refetchQueries(["Devices", gardenId]);
      },
    },
  );

  const { isLoading: isChangingValveName, mutate: mutateChangeValveName } = useMutation(
    /** @param {import("../types/global").ChangeValveNameRequest} params */
    ({ deviceId, valveId, name }) => {
      return axios.put(`${process.env.REACT_APP_GARDENER_API_END_POINT}/device/${deviceId}/valve/${valveId}/name`, {
        name,
      });
    },
    {
      onSettled: (data, vars, ctx) => {
        queryClient.refetchQueries(["Garden", gardenId]);
        queryClient.refetchQueries(["Devices", gardenId]);
      },
    },
  );

  const { isLoading: isUpdatingValveArea, mutate: mutateUpdateValveArea } = useMutation(
    /** @param {import("../types/global").UpdateValveAreaRequest} params */
    ({ deviceSerial, valveId, coordinates }) => {
      return axios.put(`${process.env.REACT_APP_PLANNER_API_END_POINT}/device/valve/area`, {
        deviceSerial,
        valveId,
        coordinates,
      });
    },
    {
      onSettled: () => {
        queryClient.refetchQueries(["Garden", gardenId]);
        queryClient.refetchQueries(["Devices", gardenId]);
      },
    },
  );

  const { isLoading: isSettingDeviceAlarm, mutateAsync: mutateAsyncSetDeviceAlarm } = useMutation(
    /** @param {import("../types/device").SetDeviceAlarmRequest} params */
    ({ deviceSerial, alarm }) => {
      return axios.put(`${process.env.REACT_APP_PLANNER_API_END_POINT}/device/water-meter/alarm`, {
        deviceSerial,
        alarm,
      });
    },
    {
      onSettled: (data, vars, ctx) => {
        queryClient.refetchQueries(["Garden", gardenId]);
        queryClient.refetchQueries(["Devices", gardenId]);
      },
    },
  );

  // --- Callbacks ---

  /** @type {import("../types/global").isConnectedToWaterMeterFunction} */
  const isConnectedToWaterMeter = useCallback(
    (deviceId) => {
      return !!devices.filter(
        (device) => device.waterMeter.status === "active" && device.waterMeter.connectedDevices && device.waterMeter.connectedDevices.includes(deviceId),
      ).length;
    },
    [devices],
  );

  /** @type {import("../types/global").getSignalStrengthForBarsFunction} */
  const getSignalStrengthForBars = useCallback((rawValue) => {
    if (rawValue === null || rawValue === undefined) {
      return { percentage: "0%", value: 0 };
    }

    const dBm = Number(rawValue);
    if (Number.isNaN(dBm)) {
      return { percentage: "0%", value: 0 };
    }

    let value;
    if (dBm >= -75) value = 5;
    else if (dBm >= -97) value = 3;
    else if (dBm >= -118) value = 2;
    else if (dBm >= -140) value = 1;
    else value = 0;

    const clamped = Math.max(-140, Math.min(-75, dBm));
    const percent = ((clamped + 140) / (140 - 75)) * 100;

    return {
      value,
      percentage: `${Math.round(percent)}%`,
    };
  }, []);

  /** @type {import("../types/global").handleDevicesReorderFunction} */
  const handleDevicesReorder = useCallback(
    (result) => {
      if (!result.destination) return; // Return if dropped outside the list
      if (!devices) throw new Error("Devices data is not available");

      const updatedItems = [...devices];
      const [reorderedItem] = updatedItems.splice(result.source.index, 1);
      updatedItems.splice(result.destination.index, 0, reorderedItem);
      mutateDevicesOrder(updatedItems.map((ud) => ud._id));
    },
    [devices, mutateDevicesOrder],
  );

  const openDeviceIrrigationReport = useCallback(
    (deviceSerial) => {
      modal.info({
        cancelButtonProps: { style: { display: "none" } },
        closable: true,
        content: <DeviceProvider.DeviceIrrigationReport deviceSerial={deviceSerial} />,
        icon: null,
        maskClosable: true,
        okButtonProps: { style: { display: "none" } },
        style: { marginTop: 0 },
        width: "80vw",
      });
    },
    [modal],
  );

  const openNemProIrrigationReport = useCallback(
    (deviceSerial) => {
      modal.info({
        cancelButtonProps: { style: { display: "none" } },
        closable: true,
        content: <DeviceProvider.NemProReport deviceSerial={deviceSerial} />,
        icon: null,
        maskClosable: true,
        okButtonProps: { style: { display: "none" } },
        style: { marginTop: 0 },
        width: "80vw",
      });
    },
    [modal],
  );

  const openNemProCalibrationModal = useCallback(
    (deviceSerial) => {
      modal.info({
        cancelButtonProps: { style: { display: "none" } },
        closable: true,
        content: <DeviceProvider.NemProCalibration deviceSerial={deviceSerial} />,
        icon: null,
        maskClosable: true,
        okButtonProps: { style: { display: "none" } },
        style: { marginTop: 0 },
        width: "20vw",
      });
    },
    [modal],
  );

  const openDeviceManagementModal = useCallback(() => {
    modal.info({
      cancelButtonProps: { style: { display: "none" } },
      closable: true,
      content: <DeviceProvider.DevicesManagement />,
      icon: null,
      maskClosable: true,
      okButtonProps: { style: { display: "none" } },
      title: (
        <Typography.Title level={5} style={{ marginBottom: 0 }}>
          {tr("Manage Garden Devices")}
        </Typography.Title>
      ),
      width: "80vw",
    });
  }, [modal]);

  const openBatteryModesModal = useCallback(() => {
    modal.info({
      cancelButtonProps: { style: { display: "none" } },
      closable: true,
      content: (
        <Row gutter={[16, 16]}>
          <Col md={12} sm={24}>
            <Typography.Title level={4}>{tr("Battery Modes Management by Garden")}</Typography.Title>

            <DeviceProvider.BatteryModesManagementByGarden />
          </Col>

          <Col md={12} sm={24}>
            <Typography.Title level={4}>{tr("Device Battery Modes Management")}</Typography.Title>

            <DeviceProvider.BatteryModesManagementByDevice />
          </Col>
        </Row>
      ),
      icon: null,
      maskClosable: true,
      okButtonProps: { style: { display: "none" } },
      style: { marginTop: 0 },
      width: windowWidth && windowWidth >= 1024 ? 920 : "80vw",
    });
  }, [modal, windowWidth]);

  /** @type {import("../types/global").updateIsDraggingEnabledFunction} */
  const updateIsDraggingEnabled = useCallback((checked) => {
    setIsDraggingEnabled(checked);
  }, []);

  const handleCloseModal = useCallback(() => {
    setSearchParams(
      (prevParams) => {
        const newParams = new URLSearchParams(prevParams);
        newParams.delete("device_id");
        newParams.delete("show_map");
        newParams.delete("show_smart_watering");

        return newParams;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  const handleShowMap = useCallback((isShowingMap) => {
    if (modalRef.current) {
      modalRef.current.update({ width: isShowingMap ? "85vw" : "70vw" });
    }
  }, []);

  /** @type {import("../types/global").openDeviceFunction} */
  const openDevice = useCallback(
    (deviceId) =>
      modal.info({
        cancelButtonProps: { style: { display: "none" } },
        content: <DeviceProvider.Device deviceId={deviceId} onCloseModal={handleCloseModal} onShowMap={handleShowMap} style={{ padding: 0 }} />,
        icon: null,
        maskClosable: true,
        okButtonProps: { style: { display: "none" } },
        onCancel: handleCloseModal,
        style: { marginTop: 0 },
        width: "60vw",
        zIndex: 1052,
      }),
    [handleCloseModal, handleShowMap, modal],
  );

  const openDeleteDeviceConfirmModal = useCallback(
    (/** @type {import("../types/global").Device["id"]} */ deviceId) => {
      modal.confirm({
        cancelText: tr("Cancel"),
        cancelButtonProps: { type: "text" },
        content: tr("Are you sure you want to remove this device"),
        okText: tr("Delete Device"),
        okType: "danger",
        onOk: async () => {
          await mutateDeleteDevice({ deviceId });
          handleCloseModal();
        },
        title: tr("Delete Device"),
        zIndex: 1053,
      });
    },
    [handleCloseModal, modal, mutateDeleteDevice],
  );

  const deviceContextValue = useMemo(() => {
    const { url: urlFromGardenDashboardResponse } = /** @type {(keyof import("../types/global").GardenDashboardResponse)[]} */ (
      Object.keys(deviceDashboard?.data || {})
    ).reduce(
      (acc, key) => {
        if (!deviceDashboard?.data) return acc;

        if (key === "success") {
          acc.success = deviceDashboard.data[key];
        } else if (key === "url") {
          acc.url = deviceDashboard.data[key];
        }
        return acc;
      },
      { success: false, url: "" },
    );

    const { url: urlFromNemProDashboardResponse } = /** @type {(keyof import("../types/global").GardenDashboardResponse)[]} */ (
      Object.keys(nemProDashboard?.data || {})
    ).reduce(
      (acc, key) => {
        if (!nemProDashboard?.data) return acc;

        if (key === "success") {
          acc.success = nemProDashboard.data[key];
        } else if (key === "url") {
          acc.url = nemProDashboard.data[key];
        }
        return acc;
      },
      { success: false, url: "" },
    );

    /** @type {import("../types/global").DeviceContextValue} */
    const value = {
      availableDevices: availableDevicesResponse?.data?.devices || [],
      device: singleDevice,
      devices,
      totalDevices,
      devicesByModel,
      device_idState: [device_id, setDeviceId],
      deviceGarden: deviceGardenResponse?.garden || undefined,
      deviceDashboardUrl: urlFromGardenDashboardResponse,
      nemProDashboardUrl: urlFromNemProDashboardResponse,
      end_dateState: [end_date, setEndDate],
      gardenOptions: gardenOptions,
      getBulkValveImages,
      getFullValveImage,
      getSignalStrengthForBars,
      handleDevicesReorder,
      isAddingDevices,
      isSettingDeviceAlarm,
      isChangingValveName,
      isChangingValveState,
      isUpdatingValveArea,
      isConnectedToWaterMeter,
      isDeletingDevice,
      isDraggingEnabled,
      isFetchingDeviceDashboard,
      isFetchingDevicesResponse,
      isFetchingSingleDevice,
      isLoadingAvailableDevices,
      isLoadingBulkValveImages,
      isLoadingFullImage,
      isLoadingUpdateDeviceName,
      isMutatingDeviceManagement,
      isMutatingDevicesOrder,
      isMutatingGardenManagement,
      isRemovingDevices,
      isFetchingNemProDashboard,
      mutateAddDevices,
      mutateAsyncSetDeviceAlarm,
      mutateChangeValveName,
      mutateChangeValveState,
      mutateUpdateValveArea,
      mutateDeleteDevice,
      mutateDeviceManagement,
      mutateGardenManagement,
      mutateRemoveDevices,
      mutateUpdateDeviceName,
      openBatteryModesModal,
      openDeleteDeviceConfirmModal,
      openDevice,
      openDeviceIrrigationReport,
      openNemProIrrigationReport,
      openNemProCalibrationModal,
      openDeviceManagementModal,
      refetchAvailableDevices,
      refetchDeviceDashboard,
      refetchNemProDashboard,
      refetchDevices,
      start_dateState: [start_date, setStartDate],
      updateIsDraggingEnabled,
      queryState,
      updateQueryParams,
    };

    return value;
  }, [
    availableDevicesResponse?.data?.devices,
    singleDevice,
    devices,
    totalDevices,
    devicesByModel,
    device_id,
    deviceGardenResponse?.garden,
    deviceDashboard?.data,
    nemProDashboard?.data,
    end_date,
    gardenOptions,
    getBulkValveImages,
    getFullValveImage,
    getSignalStrengthForBars,
    handleDevicesReorder,
    isAddingDevices,
    isSettingDeviceAlarm,
    isChangingValveName,
    isChangingValveState,
    isUpdatingValveArea,
    isConnectedToWaterMeter,
    isDeletingDevice,
    isDraggingEnabled,
    isFetchingDeviceDashboard,
    isFetchingDevicesResponse,
    isFetchingSingleDevice,
    isFetchingNemProDashboard,
    isLoadingAvailableDevices,
    isLoadingBulkValveImages,
    isLoadingFullImage,
    isLoadingUpdateDeviceName,
    isMutatingDeviceManagement,
    isMutatingDevicesOrder,
    isMutatingGardenManagement,
    isRemovingDevices,
    queryState,
    updateQueryParams,
    mutateAddDevices,
    mutateAsyncSetDeviceAlarm,
    mutateChangeValveName,
    mutateChangeValveState,
    mutateUpdateValveArea,
    mutateDeleteDevice,
    mutateDeviceManagement,
    mutateGardenManagement,
    mutateRemoveDevices,
    mutateUpdateDeviceName,
    openBatteryModesModal,
    openDeleteDeviceConfirmModal,
    openDevice,
    openDeviceIrrigationReport,
    openNemProIrrigationReport,
    openNemProCalibrationModal,
    openDeviceManagementModal,
    refetchAvailableDevices,
    refetchDeviceDashboard,
    refetchNemProDashboard,
    refetchDevices,
    start_date,
    updateIsDraggingEnabled,
  ]);

  useEffect(() => {
    const deviceIdFromUrl = searchParams.get("device_id");
    if (deviceIdFromUrl && !modalRef.current) {
      modalRef.current = openDevice(deviceIdFromUrl);
    }

    if (!deviceIdFromUrl && modalRef.current) {
      modalRef.current.destroy();
      modalRef.current = null;
    }
  }, [searchParams, openDevice]);

  return (
    <DeviceContext.Provider value={deviceContextValue}>
      {children}
      {contextHolder}
    </DeviceContext.Provider>
  );
}

function WateringStatus({ valves, aplan, remaining, commandState, wakeWindowMs, sentAt, connected }) {
  const [, tick] = useState(0);
  useEffect(() => {
    if (!commandState) return;
    const id = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [commandState]);

  if (commandState) {
    const waitedMs = Date.now() - (sentAt || 0);
    const overdue = waitedMs > wakeWindowMs;
    return (
      <Alert
        type={overdue ? "warning" : "info"}
        showIcon
        icon={<Loading3QuartersOutlined spin />}
        style={{ marginBottom: 12, borderRadius: 12 }}
        message={commandState === "starting" ? tr("Starting watering") : tr("Stopping watering")}
        description={
          overdue
            ? tr("The device hasn't responded yet. Waiting for the device to come online...")
            : tr("Waiting for the device to respond.")
        }
      />
    );
  }

  if (aplan?.watering) {
    const name = valves?.[String(aplan.valve)]?.name;
    const label = name && name !== `Zone ${aplan.valve}` ? name : `${tr("Zone")} ${aplan.valve}`;
    return (
      <Alert
        type="success"
        showIcon
        icon={<GiPlantWatering />}
        style={{ marginBottom: 12, borderRadius: 12 }}
        message={
          <Space>
            {tr("Watering active")}
            {!connected && <Tag color="orange">{tr("reconnecting")}</Tag>}
          </Space>
        }
        description={
          remaining && (
            <Space size="large">
              <Typography.Text>
                {label}: <strong>{fmt(remaining.valveSec)}</strong>
              </Typography.Text>
              <Typography.Text type="secondary">
                {tr("Total")}: {fmt(remaining.totalSec)}
              </Typography.Text>
            </Space>
          )
        }
      />
    );
  }

  return null;
}

DeviceProvider.BatteryModesManagementByGarden = function DeviceProviderBatteryModesManagement() {
  const { garden, gardenId } = useGarden();
  const { isMutatingGardenManagement, mutateGardenManagement } = useDevice();
  const [mode, setMode] = useState(
    /** @type {(import("../types/global").BatteryManagementModes[keyof import("../types/global").BatteryManagementModes])["id"]} */(garden?.mgmt?.toLocaleUpperCase()),
  );
  const [isSaved, setIsSaved] = useState(false);

  if (!garden) return null;
  if (!gardenId) return null;

  return (
    <Radio.Group
      onChange={(e) => {
        const modeKey = /** @type {(import("../types/global").BatteryManagementModes[keyof import("../types/global").BatteryManagementModes])["id"]} */ (e.target.value);
        setMode(modeKey);
      }}
      style={{ display: "block" }}
      value={mode}
    >
      <List
        bordered
        dataSource={/** @type {(keyof import("../types/global").BatteryManagementModes)[]} */ (Object.keys(BATTERY_MANAGEMENT_MODES))}
        header={
          <Row align={"middle"}>
            <Col span={12}>
              <Typography.Text strong>{tr("Battery Modes")}</Typography.Text>
            </Col>

            <Col span={12} style={{ textAlign: "right" }}>
              {isSaved && (
                <Tag closable color="success" onClose={() => setIsSaved(false)}>
                  {tr("Saved")}
                </Tag>
              )}

              <Button
                disabled={isMutatingGardenManagement || mode === garden?.mgmt?.toLocaleUpperCase()}
                onClick={() => {
                  try {
                    Promise.resolve(
                      mutateGardenManagement({
                        gardenId,
                        mode: /** @type {typeof mode} */ (mode.toLocaleLowerCase()),
                      }),
                    ).then((response) => {
                      setIsSaved(true);
                    });
                  } catch (error) {
                    throw error;
                  }
                }}
                size="small"
                type="primary"
              >
                {tr("Save")}
              </Button>
            </Col>
          </Row>
        }
        locale={{ emptyText: tr("No Devices Found") }}
        renderItem={
          /**
           * @param {keyof import("../types/global").BatteryManagementModes} key
           * @returns {import("react").JSX.Element}
           */
          (key) => (
            <List.Item actions={[<Radio value={key} disabled={isMutatingGardenManagement} />]}>
              <List.Item.Meta avatar={getBatteryModeIcon(key)} title={<Typography.Text>{tr(BATTERY_MANAGEMENT_MODES[key].id)}</Typography.Text>} />
            </List.Item>
          )
        }
        rowKey={(k) => k}
        size="small"
        style={{ borderRadius: 12, height: 235, overflow: "auto", position: "relative" }}
      />
    </Radio.Group>
  );
};

/**
 * Display a list of devices with individual battery mode selection
 * @returns {import("react").JSX.Element}
 */
DeviceProvider.BatteryModesManagementByDevice = function DeviceProviderDeviceBatteryModesList() {
  const { devices, isMutatingDeviceManagement, mutateDeviceManagement, openDeviceManagementModal } = useDevice();
  const [changedModes, setChangedModes] = useState(/** @type {Record<string, string>} */({}));
  const [isSaved, setIsSaved] = useState(false);

  return (
    <List
      bordered
      dataSource={devices.filter((device) => device.model === "LT")}
      header={
        <Row align={"middle"}>
          <Col span={12}>
            <Typography.Text strong>{tr("Devices (available for Battery Mode management)")}</Typography.Text>
          </Col>

          <Col span={12} style={{ textAlign: "right" }}>
            {isSaved && (
              <Tag closable color="success" onClose={() => setIsSaved(false)}>
                {tr("Saved")}
              </Tag>
            )}

            <Button
              disabled={isMutatingDeviceManagement || Object.keys(changedModes).length === 0}
              onClick={() => {
                try {
                  Object.entries(changedModes).forEach(([deviceSid, modeKey]) => {
                    mutateDeviceManagement({
                      deviceSid,
                      mgmtMode: modeKey,
                    });
                  });
                  setChangedModes({});
                  setIsSaved(true);
                } catch (error) {
                  throw error;
                }
              }}
              size="small"
              type="primary"
            >
              {tr("Save")}
            </Button>
          </Col>
        </Row>
      }
      locale={{
        emptyText: (
          <Space align="center" direction="vertical" size={[0, 0]} style={{ width: "100%" }}>
            <CiWarning size={48} style={{ color: "gainsboro" }} />

            <Typography.Text type="secondary">{tr("No Devices Found")}.</Typography.Text>

            <Button
              icon={<BiDevices style={{ marginRight: 6 }} />}
              onClick={openDeviceManagementModal}
              size="small"
              style={{ alignItems: "center", display: "flex", marginTop: 12 }}
              type="primary"
            >
              {tr("Manage Devices at Garden")}
            </Button>
          </Space>
        ),
      }}
      renderItem={
        /**
         * @param {import("../types/global").Device} device
         * @returns {import("react").JSX.Element}
         */
        (device) => (
          <List.Item>
            <List.Item.Meta
              avatar={<Avatar src={deviceImageMap[device.model]} />}
              title={
                <Space size="small">
                  <Tooltip title={device.deviceSerial}>
                    <span>
                      <Typography.Text>{device.Device?.name || device.deviceSerial}</Typography.Text>
                    </span>
                  </Tooltip>

                  <DeviceProvider.DeviceBattery device={device} />
                </Space>
              }
              description={
                <Select
                  disabled={isMutatingDeviceManagement}
                  onChange={(modeKey) => {
                    const currentMode = isDeviceOnMode(device?.mode, device?.period, device?.firmwareVersion);
                    if (modeKey === currentMode) {
                      setChangedModes((prev) => {
                        const newModes = { ...prev };
                        delete newModes[device.sid];
                        return newModes;
                      });
                    } else {
                      setChangedModes((prev) => ({ ...prev, [device.sid]: modeKey }));
                    }
                    setIsSaved(false);
                  }}
                  size="small"
                  style={{ width: 150 }}
                  value={changedModes[device.sid] || isDeviceOnMode(device?.mode, device?.period, device?.firmwareVersion)}
                >
                  {
                    /** @type {(keyof import("../types/global").BatteryManagementModes)[]} */ (Object.keys(BATTERY_MANAGEMENT_MODES)).map((key) => (
                    <Select.Option key={key} value={BATTERY_MANAGEMENT_MODES[key].id}>
                      <Space size="small">
                        {getBatteryModeIcon(key)}
                        {tr(BATTERY_MANAGEMENT_MODES[key].id)}
                      </Space>
                    </Select.Option>
                  ))
                  }
                </Select>
              }
            />
          </List.Item>
        )
      }
      rowKey={(device) => device._id}
      size="small"
      style={{ borderRadius: 12, height: 235, overflow: "auto" }}
    />
  );
};

/**
 * @param {Object} properties
 * @param {import("../types/global").Device["deviceSerial"]} properties.deviceSerial
 * @returns {import("react").JSX.Element | null}
 */
DeviceProvider.DeviceIrrigationReport = function DeviceProviderDeviceIrrigationReport({ deviceSerial: deviceId }) {
  const { devices, device_idState, deviceDashboardUrl, end_dateState, isFetchingDeviceDashboard, start_dateState } = useDevice();
  const [, setDeviceId] = device_idState;
  const [end_date, setEndDate] = end_dateState;
  const [start_date, setStartDate] = start_dateState;
  const [dashboardLoading, setDashboardLoading] = useState(true);

  const device = useMemo(() => devices.find((d) => d.deviceSerial === deviceId), [deviceId, devices]);
  const title = useMemo(() => `${tr("Device Irrigation Report")}: ${device?.name || device?.deviceSerial}`, [device]);

  useEffect(() => {
    setDeviceId(deviceId);
  }, [deviceId, setDeviceId]);

  // No manual refetch here: start/end dates are in the query key, so react-query
  // fetches with the current dates automatically when they change.

  useEffect(() => {
    setDashboardLoading(true);
  }, [deviceDashboardUrl]);

  if (!deviceDashboardUrl) return null;
  if (!device) return null;

  return (
    <Row align="middle" gutter={[16, 16]}>
      <Col span={24}>
        <Row align="middle" justify="space-between" style={{ marginTop: 10 }}>
          <Col>
            <Typography.Title level={5} style={{ marginBottom: 0 }}>
              {title}
            </Typography.Title>
          </Col>

          <Col>
            <DatePicker.RangePicker
              onChange={(values) => {
                if (!values?.[0]) return;
                if (!values?.[1]) return;

                setEndDate(values[1]);
                setStartDate(values[0]);
              }}
              value={[start_date, end_date]}
            />
          </Col>
        </Row>
      </Col>

      <Col span={24} style={{ position: "relative" }}>
        <Spin spinning={dashboardLoading || isFetchingDeviceDashboard}>
          <iframe
            allowTransparency
            style={{
              width: "100%",
              height: "calc(100vh - 300px)", // subtract header + margins
              border: "none",
            }}
            src={deviceDashboardUrl}
            title={title}
            onLoad={() => setDashboardLoading(false)}
          />
        </Spin>
      </Col>
    </Row>
  );
};

/**
 * @param {Object} properties
 * @param {import("../types/global").Device["deviceSerial"]} properties.deviceSerial
 * @returns {import("react").JSX.Element | null}
 */
DeviceProvider.NemProReport = function DeviceProviderNemProReport({ deviceSerial: deviceId }) {
  const { devices, device_idState, nemProDashboardUrl, end_dateState, refetchNemProDashboard, start_dateState } = useDevice();
  const [, setDeviceId] = device_idState;
  const [end_date, setEndDate] = end_dateState;
  const [start_date, setStartDate] = start_dateState;

  const device = useMemo(() => devices.find((d) => d.deviceSerial === deviceId), [deviceId, devices]);
  const title = useMemo(() => `${tr("Humidity Report")}: ${device?.name || device?.deviceSerial}`, [device]);

  const { mutate: mutateCheckHumidity, isLoading: isMutatingHumidity } =
    /** @type {import("react-query").UseMutationResult<import("axios").AxiosResponse<any, any>, unknown, any, unknown>} */ (
      useMutation((deviceSerial) => axios.post(`${process.env.REACT_APP_PLANNER_API_END_POINT}/device/humidity`, { deviceSerial }), {
        onSuccess: () => {
          refetchNemProDashboard();
        },
        onError: (error) => {
          console.error("Humidity Check Failed:", error);
        },
      })
    );

  useEffect(() => {
    setDeviceId(deviceId);
  }, [deviceId, setDeviceId]);

  if (!nemProDashboardUrl) return null;
  if (!device) return null;

  return (
    <Row align="middle" gutter={[16, 16]}>
      <Col span={24}>
        <Row
          align="middle"
          justify="space-between"
          style={{
            padding: "8px 0",
          }}
        >
          {/* Left: Title */}
          <Col>
            <Typography.Title level={5} style={{ margin: 0 }}>
              {title}
            </Typography.Title>
          </Col>

          {/* Right: Controls */}
          <Col>
            <Row align="middle" gutter={12} wrap={false}>
              <Col>
                <DatePicker.RangePicker
                  onChange={(values) => {
                    if (!values?.[0] || !values?.[1]) return;
                    setStartDate(values[0]);
                    setEndDate(values[1]);
                  }}
                  value={[start_date, end_date]}
                  style={{ width: 260 }}
                />
              </Col>

              <Col>
                <Button
                  type="primary"
                  onClick={() => {
                    mutateCheckHumidity(deviceId);
                  }}
                >
                  {tr("Check Humidity")}
                </Button>
              </Col>

              <Col>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={() => {
                    // Whatever reload means for your dashboard
                    // e.g., refresh iframe by changing URL or key
                    refetchNemProDashboard();
                  }}
                />
              </Col>
            </Row>
          </Col>
        </Row>
      </Col>

      <Col span={24}>
        <iframe
          allowTransparency
          height="600px"
          src={nemProDashboardUrl}
          title={title}
          width="100%"
          style={{
            borderRadius: 8,
            border: "1px solid #e3e3e3",
          }}
        />
      </Col>
    </Row>
  );
};

/**
 * @param {Object} properties
 * @param {import("../types/global").Device["deviceSerial"]} properties.deviceSerial
 * @returns {import("react").JSX.Element | null}
 */
DeviceProvider.NemProCalibration = function DeviceProviderNemProCalibration({ deviceSerial: deviceId }) {
  const { devices, device_idState } = useDevice();
  const device = useMemo(() => devices.find((d) => d.deviceSerial === deviceId), [deviceId, devices]);
  const queryClient = useQueryClient();

  const [minHumidity, setMinHumidity] = useState(device?.minHumidity || 0);
  const [minSalinity, setMinSalinity] = useState(device?.minSalinity || 0);

  const { mutate: mutateCalibrateNemPro, isLoading: isCalibratingNemPro } =
    /** @type {import("react-query").UseMutationResult<import("axios").AxiosResponse<any, any>, unknown, any, unknown>} */ (
      useMutation(
        ({ deviceSerial, minHumidity, minSalinity }) =>
          axios.put(`${process.env.REACT_APP_PLANNER_API_END_POINT}/device/humidity/threshold`, { deviceSerial, minHumidity, minSalinity }),
        {
          onSuccess: () => {
            queryClient.invalidateQueries("Garden");
          },
          onError: (error) => {
            console.error("Humidity Check Failed:", error);
          },
        },
      )
    );

  return (
    <>
      <Col>
        <Typography.Title level={5} style={{ margin: 0 }}>
          {tr("Device Calibration")}
        </Typography.Title>
      </Col>
      <Row align="middle" gutter={[16, 16]}>
        <Col span={24}>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {tr("Minimum Humidity")}
          </Typography.Text>
          <InputNumber style={{ width: "100%" }} value={minHumidity} onChange={(v) => setMinHumidity(v ?? 0)} placeholder="Minimum Humidity" />
        </Col>
        <Col span={24}>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {tr("Minimum EC")}
          </Typography.Text>
          <InputNumber style={{ width: "100%" }} value={minSalinity} onChange={(v) => setMinSalinity(v ?? 0)} placeholder="Minimum EC" />
        </Col>
        <Col span={24}>
          <Button type="primary" block onClick={() => mutateCalibrateNemPro({ deviceSerial: deviceId, minHumidity, minSalinity })}>
            {tr("Submit")}
          </Button>
        </Col>
      </Row>
    </>
  );
};

/**
 * Display a list of devices with drag-and-drop reordering
 * @param {{height: string | number}} props
 * @returns {import("react").JSX.Element | null}
 */
DeviceProvider.DevicesList = function DeviceProviderDevicesList({ height = 300 }) {
  const { isDraggingEnabled, updateIsDraggingEnabled, openNemProIrrigationReport } = useDevice();
  const { devices, handleDevicesReorder, isMutatingDevicesOrder } = useDevice();
  const [, setSearchParams] = useSearchParams();

  if (!devices.length)
    return (
      <List
        bordered
        dataSource={devices}
        header={
          <Row align="middle" justify="space-between">
            <Col>
              <Typography.Title level={5} style={{ margin: 0 }}>
                {tr("Device List")} ({devices.length})
              </Typography.Title>
            </Col>

            <Col>
              <DeviceProvider.DevicesMenu />
            </Col>
          </Row>
        }
        locale={{
          emptyText: (
            <Space align="center" direction="vertical" size={[0, 0]}>
              <CiWarning size={48} style={{ color: "gainsboro" }} />

              <Typography.Text type="secondary">{tr("No Devices Found")}.</Typography.Text>
            </Space>
          ),
        }}
        style={{
          height,
          position: "relative",
          overflow: "auto",
          transition: "background 0.2s ease",
          borderRadius: 12,
        }}
      />
    );

  return (
    <DragDropContext onDragEnd={handleDevicesReorder}>
      <Droppable droppableId="devices-droppable">
        {(dropProvided, dropSnapshot) => (
          <ul
            className="ant-list-items ant-list-split ant-list-bordered"
            ref={dropProvided.innerRef}
            {...dropProvided.droppableProps}
            style={{
              background: dropSnapshot.isDraggingOver ? "rgba(24, 144, 255, 0.06)" : "transparent",
              height,
              position: "relative",
              overflow: "auto",
              transition: "background 0.2s ease",
              borderRadius: 12,
            }}
          >
            <div className="ant-list-header">
              <Row align="middle" justify="space-between">
                {/* LEFT SIDE: Device List text + Optimization settings */}
                <Col>
                  <Space align="center" size="middle">
                    <Typography.Title level={5} style={{ margin: 0 }}>
                      {tr("Device List")} ({devices.length})
                    </Typography.Title>
                    {devices?.filter((t) => t.model !== "BL").length > 0 && <DeviceProvider.OptimizationSettings />}
                  </Space>
                </Col>

                {/* RIGHT SIDE: Switch + Menu */}
                <Col>
                  <Space size="small">
                    {isMutatingDevicesOrder && <Loading3QuartersOutlined spin />}

                    <Tooltip title={tr("Reorder Devices")}>
                      <Switch checked={isDraggingEnabled} onChange={updateIsDraggingEnabled} size="small" />
                    </Tooltip>

                    <DeviceProvider.DevicesMenu />
                  </Space>
                </Col>
              </Row>
            </div>

            {devices.map((device, index) => (
              <Draggable draggableId={String(device.id)} index={index} isDragDisabled={!isDraggingEnabled} key={device.id}>
                {(dragProvided, dragSnapshot) => (
                  <li
                    ref={dragProvided.innerRef}
                    {...dragProvided.draggableProps}
                    // `ant-list-item` is the <li>; inside we place the meta and extra areas
                    className="ant-list-item"
                    {...dragProvided.dragHandleProps}
                  >
                    <div className="ant-list-item-meta" style={{ alignItems: "center" }}>
                      <div className="ant-list-item-meta-avatar">
                        <Space size="small">
                          {isDraggingEnabled && <GrDrag className="text-green-brand" style={{ cursor: "grab" }} />}
                          <Avatar src={deviceImageMap[device.model]} />
                        </Space>
                      </div>
                      <div className="ant-list-item-meta-content">
                        <div className="ant-list-item-meta-title">
                          {/* Make the title area the drag handle (or move props to an icon) */}
                          <Space>
                            <Typography.Text
                              onClick={() => {
                                if (device.model !== "NEM") {
                                  setSearchParams(
                                    (previousSearchParams) => {
                                      const newSearchParams = new URLSearchParams(previousSearchParams);
                                      newSearchParams.set("device_id", device.id);
                                      newSearchParams.set("show_map", "true");
                                      if (!!device.waterMeter.connectedDevices.length) newSearchParams.set("show_smart_watering", "true");
                                      return newSearchParams;
                                    },
                                    { replace: true },
                                  );
                                } else {
                                  openNemProIrrigationReport(device.deviceSerial);
                                }
                              }}
                              style={{ cursor: "pointer", fontWeight: 500 }}
                            >
                              {device.Device?.name || device.Device?.serial || device.deviceSerial}
                            </Typography.Text>
                          </Space>
                        </div>
                        <div className="ant-list-item-meta-description">
                          <Space size="small">
                            <DeviceProvider.DeviceBattery device={device} />
                            <DeviceProvider.DeviceSignal device={device} />
                            <DeviceProvider.DeviceStatus device={device} />
                          </Space>
                        </div>
                      </div>
                    </div>

                    <div className="ant-list-item-action">
                      <DeviceProvider.DeviceMenu device={device} />
                    </div>
                  </li>
                )}
              </Draggable>
            ))}
            {dropProvided.placeholder}
          </ul>
        )}
      </Droppable>
    </DragDropContext>
  );
};

DeviceProvider.DevicesManagement = function DeviceProviderDevicesManagement() {
  const { availableDevices, devices, isLoadingAvailableDevices, isAddingDevices, isRemovingDevices, mutateAddDevices, mutateRemoveDevices, refetchAvailableDevices } =
    useDevice();

  /**
   * @param {string} text
   * @returns {string}
   */
  function transformTurkish(text) {
    if (text) {
      return text.replace(/[CcÇçGgĞğIıİiOoÖöSsŞşUuÜü]/g, "_");
    } else {
      return "";
    }
  }

  /** @type {Exclude<import("antd").TransferProps<import("../types/global").Device>["filterOption"], undefined>} */
  function filterOption(inputValue, item) {
    return RegExp(`.*${transformTurkish(inputValue)}.*`, "gi").test(transformTurkish(item.name));
  }

  useEffect(() => {
    refetchAvailableDevices();
  }, [refetchAvailableDevices]);

  return (
    <Transfer
      dataSource={availableDevices}
      disabled={isAddingDevices || isLoadingAvailableDevices || isRemovingDevices}
      filterOption={filterOption}
      listStyle={{ height: 500, width: "100%" }}
      locale={{
        itemUnit: tr("Device"),
        itemsUnit: tr("Device"),
        notFoundContent: isLoadingAvailableDevices ? <Loading3QuartersOutlined spin /> : tr("No Devices Found"),
        searchPlaceholder: tr("Search Devices"),
        selectAll: tr("Select All"),
        selectInvert: tr("Select Invert"),
      }}
      onChange={(targetKeys, direction, moveKeys) => {
        switch (direction) {
          case "left":
            mutateRemoveDevices(moveKeys);
            break;

          case "right":
            mutateAddDevices(moveKeys);
            break;

          default:
            throw new Error(tr("UNKNOWN_TRANSFER_DIRECTION"));
        }
      }}
      render={(device) => <DeviceProvider.Title device={device} />}
      rowKey={(device) => device._id}
      showSearch
      targetKeys={devices.map((d) => d._id)}
      titles={[
        <Tooltip placement="topRight" title={tr("Devices in tenant but not in a garden")}>
          <span>
            <Space>
              <Typography.Text style={{ marginBottom: 0 }}>{tr("Assignable Devices")}</Typography.Text>
              <InfoCircleOutlined style={{ color: "#3F7C88", transform: "translate(0,-3px)" }} />
            </Space>
          </span>
        </Tooltip>,

        <Tooltip placement="topRight" title={tr("Devices assigned to this park")}>
          <span>
            <Space>
              <Typography.Text style={{ marginBottom: 0 }}>{tr("Assigned Devices")}</Typography.Text>
              <InfoCircleOutlined style={{ color: "#3F7C88", transform: "translate(0,-3px)" }} />
            </Space>
          </span>
        </Tooltip>,
      ]}
    />
  );
};

/**
 *
 * @param {import("react").PropsWithChildren<{device: import("../types/global").Device}>} props
 * @returns {import("react").JSX.Element}
 */
DeviceProvider.DeviceMenu = function DeviceProviderDeviceMenu({ device }) {
  const { isConnectedToWaterMeter, isDraggingEnabled, openDeviceIrrigationReport, openNemProIrrigationReport, openNemProCalibrationModal } = useDevice();
  const isDisabled = useMemo(
    () => device.deviceSerial.startsWith("BLT") && !isConnectedToWaterMeter(device._id),
    [device.deviceSerial, device._id, isConnectedToWaterMeter],
  );
  const [, setSearchParams] = useSearchParams();

  return (
    <Dropdown
      disabled={isDraggingEnabled}
      overlay={
        <Menu>
          {device.model !== "NEM" && (
            <>
              <Menu.Item
                disabled={device.deviceSerial.startsWith("BLT") && !isConnectedToWaterMeter(device._id)}
                icon={<FaChartBar />}
                key={`${device.id}-devideMenuItem1`}
                onClick={() => openDeviceIrrigationReport(device.deviceSerial)}
              >
                {isDisabled ? (
                  <Tooltip placement="topRight" title={tr("Irrigation report is available only for BLT devices connected to a water meter.")}>
                    <span>
                      <Space>
                        {tr("Irrigation Report")}

                        <WarningOutlined style={{ transform: "translate(0, -2px)" }} />
                      </Space>
                    </span>
                  </Tooltip>
                ) : (
                  tr("Irrigation Report")
                )}
              </Menu.Item>

              <Menu.Item
                icon={<EyeFilled />}
                key={`${device.id}-devideMenuItem2`}
                onClick={() =>
                  setSearchParams(
                    (previousSearchParams) => {
                      const newSearchParams = new URLSearchParams(previousSearchParams);
                      newSearchParams.set("device_id", device.id);
                      newSearchParams.set("show_map", "true");
                      if (!!device.waterMeter.connectedDevices.length) newSearchParams.set("show_smart_watering", "true");
                      return newSearchParams;
                    },
                    { replace: true },
                  )
                }
              >
                {tr("Device Details")}
              </Menu.Item>
            </>
          )}
          {device.model === "NEM" && (
            <>
              <Menu.Item icon={<EyeFilled />} key={`${device.id}-devideMenuItem3`} onClick={() => openNemProIrrigationReport(device.deviceSerial)}>
                {tr("Device Details")}
              </Menu.Item>
              <Menu.Item icon={<SettingFilled />} key={`${device.id}-devideMenuItem4`} onClick={() => openNemProCalibrationModal(device.deviceSerial)}>
                {tr("Calibrate")}
              </Menu.Item>
            </>
          )}
        </Menu>
      }
      trigger={["click"]}
    >
      <Button icon={<MoreOutlined />} />
    </Dropdown>
  );
};

/**
 * @returns {import("react").JSX.Element | null}
 */
DeviceProvider.DevicesMenu = function DeviceProviderDevicesMenu() {
  const { garden } = useGarden();
  const { openBatteryModesModal, openDeviceManagementModal } = useDevice();

  return (
    <Dropdown
      overlay={
        <Menu>
          <Menu.Item icon={<BiDevices />} key={`${garden?.id}-devicesMenuItem1`} onClick={openDeviceManagementModal}>
            {tr("Manage Devices at Garden")}
          </Menu.Item>

          <Menu.Item icon={<BsBatteryHalf />} key={`${garden?.id}-devicesMenuItem2`} onClick={openBatteryModesModal}>
            {tr("Manage Battery Modes")}
          </Menu.Item>
        </Menu>
      }
      trigger={["click"]}
    >
      <Button icon={<MoreOutlined />} />
    </Dropdown>
  );
};

/**
 * List battery level of a device
 * @param {import("react").PropsWithChildren<{device: import("../types/global").Device}>} props
 * @returns {import("react").JSX.Element | null}
 */
DeviceProvider.DeviceBattery = function DeviceProviderDeviceBattery({ device }) {
  const percentage = getNormalizedBatteryPct(device);

  function getBatteryIcon(/** @type {number} */ percentage) {
    if (percentage >= 100) return <FaBatteryFull color="green" size={16} />;
    if (percentage >= 75) return <FaBatteryThreeQuarters color="green" size={16} />;
    if (percentage >= 50) return <FaBatteryHalf color="orange" size={16} />;
    if (percentage >= 20) return <FaBatteryQuarter color="red" size={16} />;
    return <FaBatteryEmpty color="red" size={16} />;
  }

  // No battery devices
  if (percentage === null) {
    return <FaPlug color="green" size={16} />;
  }

  return <Tooltip title={Intl.NumberFormat("tr-TR", { style: "percent" }).format(percentage / 100)}>{getBatteryIcon(percentage)}</Tooltip>;
};

/**
 * Display latest connection time of a BL device or last alive time of other devices
 * @param {import("react").PropsWithChildren<{device: import("../types/global").Device}>} props
 * @returns {import("react").JSX.Element | null}
 */
DeviceProvider.DeviceLastLoginAt = function DeviceProviderDeviceLastLoginAt({ children, device }) {
  if (device.model !== "BL") return null;
  if (!device?.lastLoginAt) return tr("MISSING_LAST_LOGIN_AT");

  return (
    <Tooltip
      overlay={
        <Space size={0} direction="vertical">
          <Typography.Text style={{ color: "white" }}>Son bağlantı {dayjs(device.lastLoginAt).format("DD/MM HH:mm")} tarihinde</Typography.Text>
          <Typography.Text style={{ color: "white" }}>{device.lastLoginBy} tarafından</Typography.Text>
        </Space>
      }
    >
      {children}
    </Tooltip>
  );
};

/**
 * @param {import("react").PropsWithChildren<{device: import("../types/global").Device}>} props
 * @returns {import("react").JSX.Element | null}
 */
DeviceProvider.DeviceSignal = function DeviceProviderDeviceSignal({ device }) {
  const { getSignalStrengthForBars } = useDevice();

  if (device.model === "BL" || device.model === "EL") return null;

  const signalInfo = getSignalStrengthForBars(device.signal);

  if (!signalInfo || signalInfo.value === -1) return null;

  function getSignalIcon(strength) {
    switch (strength) {
      case 0:
        return <BiNoSignal color="gray" size={16} />;
      case 1:
        return <BiSignal1 color="red" size={16} />;
      case 2:
        return <BiSignal2 className="text-yellow-500" size={16} />;
      case 3:
        return <BiSignal3 className="text-yellow-500" size={16} />;
      case 4:
        return <BiSignal4 className="text-green-500" size={16} />;
      case 5:
        return <BiSignal5 className="text-green-500" size={16} />;
      default:
        return <BiNoSignal color="gray" size={16} />;
    }
  }

  return (
    <Tooltip title={signalInfo.percentage}>
      <div style={{ height: 16, width: 16, position: "relative" }}>
        <BiSignal5 color="lightgray" size={16} style={{ position: "absolute", top: 0, left: 0, zIndex: 0 }} />
        <div style={{ position: "absolute", top: 0, left: 0, zIndex: 1 }}>{getSignalIcon(signalInfo.value)}</div>
      </div>
    </Tooltip>
  );
};

/**
 * Display latest connection time of a BL device or last alive time of other devices
 * @param {import("react").PropsWithChildren<{device: import("../types/global").Device}>} props
 * @returns {import("react").JSX.Element | null}
 */
DeviceProvider.DeviceState = function DeviceProviderDeviceConnectionTime({ children, device }) {
  if (device.model === "BL")
    return (
      <Tooltip
        overlay={
          <Space size={0} direction="vertical">
            <Typography.Text style={{ color: "white" }}>
              {tr("Last Connection Time")} {formatUtcWithOffset(device.lastLoginAt, device.timezoneOffset, device.timezone)}
            </Typography.Text>
            <Typography.Text style={{ color: "white" }}>{tr("Connected by")} {device.lastLoginBy}</Typography.Text>
          </Space>
        }
      >
        {children}
      </Tooltip>
    );

  const lastConDate = device?.lastOnlineAt
    ? formatUtcWithOffset(device.lastOnlineAt, device.timezoneOffset, device.timezone)
    : device?.lastAliveAt
      ? formatUtcWithOffset(device.lastAliveAt, device.timezoneOffset, device.timezone)
      : "-";

  return <Tooltip overlay={<Typography.Text style={{ color: "white" }}>{lastConDate}</Typography.Text>}>{children}</Tooltip>;
};

/**
 * Status indicator for devices other than BL model
 * @param {import("react").PropsWithChildren<{device: import("../types/global").Device}>} props
 * @returns {import("react").JSX.Element | null}
 */
DeviceProvider.DeviceStatus = function DeviceProviderDeviceStatus({ device }) {
  if (device.model === "BL")
    return (
      <DeviceProvider.DeviceState device={device}>
        <IoBluetooth color="blue" size={16} />
      </DeviceProvider.DeviceState>
    );

  switch (device.alive) {
    case "alive":
      return (
        <DeviceProvider.DeviceState device={device}>
          <IoCloudDoneOutline color="green" size={16} />
        </DeviceProvider.DeviceState>
      );

    case "dead":
      return (
        <DeviceProvider.DeviceState device={device}>
          <IoCloudOfflineOutline color="red" size={16} />
        </DeviceProvider.DeviceState>
      );

    default:
      return <IoCloud color="gray" size={16} />;
  }
};

/**
 * Device screen which uses PageHeader from Ant Design.
 * @param {import("react").PropsWithChildren<{ deviceId: import("../types/global").Device["id"], onCloseModal?:() => void; onShowMap: (isShowingMap: boolean) => void} & import("antd").PageHeaderProps>} properties
 * @returns {import("react").JSX.Element | null}
 */
DeviceProvider.Device = function DeviceProviderDevice({ children, deviceId, onCloseModal, onShowMap, ...pageHeaderProps }) {
  const { devices } = useDevice();
  const { isLoadingUpdateDeviceName, mutateUpdateDeviceName, deviceGarden } = useDevice();
  const [searchParams] = useSearchParams();
  const isShowingMap = useMemo(() => searchParams.get("show_map") === "true", [searchParams]);
  const isShowingSmartWatering = useMemo(() => searchParams.get("show_smart_watering") === "true", [searchParams]);
  const isShowingAlarmLimit = useMemo(() => searchParams.get("show_alarm_limit") === "true", [searchParams]);
  const navigate = useNavigate();
  const [activeValveId, setActiveValveId] = useState(/** @type {string | null} */(null));
  const isGardenRoute = window.location.pathname.includes("/gardens/");

  /** @type {import("../types/global").Device | undefined} */
  const device = useMemo(() => devices.find((d) => d.id === deviceId), [deviceId, devices]);

  const firstValveId = useMemo(() => {
    if (!device?.valves) return null;

    try {
      const parsedValves = device.valves;
      const visibleValveKeys = Object.keys(parsedValves).slice(0, device.valveCount);
      return visibleValveKeys.length ? visibleValveKeys[0] : null;
    } catch (error) {
      return null;
    }
  }, [device?.valveCount, device?.valves]);

  /** @type {import("../types/global").Device[]} */
  const otherDevices = useMemo(() => devices.filter((d) => d.id !== deviceId), [deviceId, devices]);

  /** @type {import("react").MutableRefObject<import("../types/global").Device["model"]>} */
  const deviceModelRef = useRef(device?.model || "EL");

  useEffect(() => {
    if (typeof onShowMap === "function") {
      const showMapParam = searchParams.get("show_map");
      onShowMap(showMapParam === "true");
    }
  }, [onShowMap, searchParams]);

  useEffect(() => {
    setActiveValveId((prevActiveValveId) => {
      if (!firstValveId) return null;
      if (!prevActiveValveId) return firstValveId;

      if (!device?.valves) return firstValveId;

      try {
        const parsedValves = device.valves;
        const visibleValveKeys = Object.keys(parsedValves).slice(0, device.valveCount);
        return visibleValveKeys.includes(String(prevActiveValveId)) ? prevActiveValveId : firstValveId;
      } catch (error) {
        return firstValveId;
      }
    });
  }, [device?.valveCount, device?.valves, firstValveId]);

  // if (!garden) return null; // Removed dependency on garden
  if (!deviceModelRef.current) return null;

  if (!device) return <DeviceProvider.NoDevice deviceId={deviceId} deviceModel={deviceModelRef.current} onCloseModal={onCloseModal} />;

  return (
    <PageHeader
      {...pageHeaderProps}
      backIcon={<img alt={device.Device?.name || device.deviceSerial} style={{ border: "none", height: 32, width: 32 }} src={deviceImageMap[device.model]} />}
      extra={[<DeviceProvider.Menu device={device} key="device-menu" onCloseModal={onCloseModal} />]}
      ghost={true}
      onBack={() => null}
      subTitle={
        <Col lg={16} md={24} sm={24} xl={10} xs={24} xxl={6} style={{ display: "flex", alignItems: "center" }}>
          {deviceGarden && (
            <Button type="default" onClick={() => navigate("/control-center/gardens/" + deviceGarden.id)}>
              <Typography.Text>{tr("Show Garden")}</Typography.Text>{" "}
              <Typography.Text>({deviceGarden.name.length > 15 ? deviceGarden.name.slice(0, 15) + "..." : deviceGarden.name})</Typography.Text>
            </Button>
          )}
        </Col>
      }
      title={
        <Row align="middle">
          <Typography.Title
            disabled={isLoadingUpdateDeviceName}
            editable={{
              icon: <EditOutlined style={{ transform: "translate(0, -2px)" }} />,
              onChange: (value) => {
                if (!device.Device) return;

                mutateUpdateDeviceName({
                  deviceId: String(device.Device.id),
                  deviceName: value,
                });
              },
            }}
            level={5}
            style={{ marginBlock: 0, transition: "none" }}
          >
            {device.Device?.name || device.deviceSerial}
          </Typography.Title>
        </Row>
      }
    >
      <Row gutter={10}>
        {isShowingSmartWatering && (
          <Col span={24}>
            <DeviceProvider.SmartWaterMeter device={device} gardenId={deviceGarden?.id} isShowing={isShowingSmartWatering} style={{ marginBottom: 10 }} />
          </Col>
        )}

        {device?.waterMeter?.status === "active" && isShowingSmartWatering && (
          <Col span={24}>
            <DeviceProvider.AlarmLimit device={device} style={{ marginBottom: 10 }} />
          </Col>
        )}

        <Col span={isShowingMap ? 13 : 24}>
          <DeviceProvider.Fields device={device} height={500} activeValveId={activeValveId} onActiveValveChange={setActiveValveId} />
        </Col>

        {isShowingMap && (
          <Col span={11} style={{ borderRadius: 12, overflow: "hidden" }}>
            <MapContainer center={[30, -41]} style={{ height: 500 }} zoom={2} zoomControl={false}>
              <DeviceProvider.InvalidateMapSize when={isShowingMap} />
              <MapsProvider>
                <MapsProvider.Container>
                  <MapsProvider.GardenLayersControl>
                    {isGardenRoute && <MapsProvider.GardenAreas />}
                    <MapsProvider.DeviceOtherMarkers checked={false} otherDevices={otherDevices} />
                    <MapsProvider.DeviceMarker device={device} />
                    <MapsProvider.Valves device={device} activeValveId={activeValveId} onActiveValveChange={setActiveValveId} />
                  </MapsProvider.GardenLayersControl>
                </MapsProvider.Container>
              </MapsProvider>
            </MapContainer>
          </Col>
        )}
      </Row>
    </PageHeader>
  );
};

DeviceProvider.InvalidateMapSize = function InvalidateMapSize({ when }) {
  const map = useMap();

  useEffect(() => {
    if (!when) return;

    // allow AntD layout + modal animation to finish
    const id = setTimeout(() => {
      map.invalidateSize();
    }, 0);

    return () => clearTimeout(id);
  }, [when, map]);

  return null;
};

/**
 * @param {{
 *  device: import("../types/global").Device,
 *  height?: import("react").CSSProperties["height"],
 *  activeValveId?: string | number | null,
 *  onActiveValveChange?: (valveId: string | number | null) => void
 * }} parameters
 * @returns {import("react").JSX.Element | null}
 */
DeviceProvider.Fields = function DeviceProviderFields({ device, height = 500, activeValveId = null, onActiveValveChange = () => { } }) {
  const [valveImagesMap, setValveImagesMap] = useState(/** @type {Record<string, string>} */({}));
  const queryClient = useQueryClient();
  const { isChangingValveName, isChangingValveState, mutateChangeValveName, mutateChangeValveState } = useDevice();
  const [manualOpen, setManualOpen] = useState(false);

  /** @type {Record<string, number>} */
  const [manualDurations, setManualDurations] = useState({});

  const isBluetooth = device.model === "BL" || device.deviceSerial.startsWith("BLT");
  const { aplan, connected, supported } = useAplanStream(device.sid, manualOpen && !isBluetooth);
  const remaining = useLiveRemaining(aplan);

  /** null | "starting" | "stopping" */
  const [commandState, setCommandState] = useState(null);
  const commandSentAt = useRef(0);

  const liveEnabled = !isBluetooth && supported;

  // Live truth when the stream is available; stale API value otherwise.
  const liveWatering = (liveEnabled && aplan) ? aplan.watering : device?.isWatering === true;

  // Wake latency depends on power mode — reuse the helper already in this file.
  const wakeWindowMs = useMemo(() => {
    const mode = isDeviceOnMode(device?.mode, device?.period, device?.firmwareVersion);
    if (mode === "real_time") return 20000;
    if (mode === "battery_saver") return 240000;
    return 120000;
  }, [device?.mode, device?.period, device?.firmwareVersion]);

  // Clear the pending state once the device confirms.
  // A stale snapshot (from a reconnect) must not count as confirmation.
  useEffect(() => {
    if (!commandState || !aplan || aplan.stale) return;
    if (commandState === "starting" && aplan.watering) setCommandState(null);
    if (commandState === "stopping" && !aplan.watering) setCommandState(null);
  }, [aplan, commandState]);

  const prevWateringRef = useRef(/** @type {boolean | null} */(null));

  useEffect(() => {
    if (!liveEnabled || !aplan || aplan.watering === undefined) return;

    const isWatering = aplan.watering === true;

    // A stale snapshot (reconnect / remount) seeds the baseline but must not
    // drive a cache write — it can be an old Redis value.
    if (aplan.stale) {
      if (prevWateringRef.current === null) prevWateringRef.current = isWatering;
      return;
    }

    if (prevWateringRef.current === isWatering) return;
    prevWateringRef.current = isWatering;

    queryClient.setQueriesData("Devices", (old) => {
      const list = old?.data?.devices;
      if (!Array.isArray(list)) return old;
      let changed = false;
      const devices = list.map((d) => {
        if (d._id !== device._id || d.isWatering === isWatering) return d;
        changed = true;
        return { ...d, isWatering };
      });
      return changed ? { ...old, data: { ...old.data, devices } } : old;
    });

    queryClient.invalidateQueries("Devices");
  }, [aplan, liveEnabled, queryClient, device._id]);

  useEffect(() => {
    prevWateringRef.current = null;
  }, [device.sid]);

  // Drop pending state if the panel closes or the stream is off.
  useEffect(() => {
    if (!manualOpen) setCommandState(null);
  }, [manualOpen]);

  const openManualWatering = () => {
    const initial = {};
    Object.keys(valves)
      .slice(0, device.valveCount)
      .forEach((valveId) => {
        initial[valveId] = 0;
      });

    setManualDurations(initial);
    setManualOpen((o) => !o);
  };

  const stopManualWatering = () => {
    testWatering.mutate({
      command: "stop",
      valve: null,
    });
  };

  const buildDurationsArray = () => {
    const durations = Array(8).fill(0);

    Object.entries(manualDurations).forEach(([valveId, duration]) => {
      const index = Number(valveId) - 1;

      if (index >= 0 && index < 8 && Number.isInteger(duration) && duration >= 0) {
        durations[index] = duration;
      }
    });

    return durations;
  };

  const valves = useMemo(() => {
    const rawValves = device?.valves ?? device?.Device?.valves;

    if (!rawValves) {
      throw new Error(tr("Device valves data is missing"));
    }

    // Handle stringified JSON from device.Device.valves
    if (typeof rawValves === "string") {
      try {
        return JSON.parse(rawValves);
      } catch (error) {
        throw new Error(tr("Failed to parse device valves data"));
      }
    }

    return rawValves;
  }, [device?.valves, device?.Device?.valves]);

  const valveIdsWithImages = useMemo(() => {
    /** @type {string[]} */
    let images = [];

    if (!Object.keys(valves).length) return images;

    return Object.keys(valves)
      .slice(0, device.valveCount)
      .filter((valveKey) => valves[valveKey].image);
  }, [device.valveCount, valves]);

  useQueries(
    valveIdsWithImages.map((valveId) => ({
      queryKey: ["ValveImage", device.Device?.id, valveId],
      queryFn: () =>
        axios.get(`${process.env.REACT_APP_GARDENER_API_END_POINT}/device/${device.Device?.id}/valve/${valveId}/image`, { responseType: "blob" }).then((res) => res.data),
      enabled: !!device.Device?.id && valveIdsWithImages.length > 0,
      staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
      cacheTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
      retry: 1,
      refetchOnWindowFocus: false,
      onSuccess: (/** @type {import("axios").AxiosResponse<Blob>} */ data) => {
        if (data instanceof Blob) setValveImagesMap((prev) => ({ ...prev, [valveId]: URL.createObjectURL(data) }));
      },
      onError: (/** @type {unknown} */ error) => {
        console.error(`Failed to load image for valve ${valveId}:`, error);
        setValveImagesMap((prev) => {
          const newMap = { ...prev };
          delete newMap[valveId];
          return newMap;
        });
      },
    })),
  );

  const testWatering = useMutation(
    /**
     * @param {import("../types/device").TestWateringVariables} vars
     * @returns {Promise<import("../types/device").TestWateringResponse>}
     */
    (vars) => {
      const { valve, command, durations = [0, 0, 0, 0, 0, 0, 0, 0] } = vars;

      return axios
        .post(`${process.env.REACT_APP_PLANNER_API_END_POINT}/plan/test/watering`, {
          deviceId: device?._id,
          valve,
          command,
          durations,
        })
        .then((res) => res.data);
    },
    {
      /**
       * @param {import("../types/device").TestWateringResponse} _data
       * @param {import("../types/device").TestWateringVariables} vars
       */
      onSuccess: (_data, vars) => {
        setCommandState(vars.command === "stop" ? "stopping" : "starting");
        commandSentAt.current = Date.now();
        queryClient.invalidateQueries("Devices");
      },
      onError: () => {
        notification.error({
          message: tr("Failed to send test command"),
        });
      },
    },
  );

  const deleteValveImage = useMutation(
    /**
     * @param {{ valveId: string }} vars
     * @returns {Promise<void>}
     */
    ({ valveId }) => {
      return axios.delete(`${process.env.REACT_APP_GARDENER_API_END_POINT}/device/${device.Device?.id}/valve/${valveId}/image`);
    },
    {
      onSuccess: (_, { valveId }) => {
        notification.success({
          message: tr("Image deleted"),
          style: { zIndex: 9999 },
        });

        if (valveImagesMap[valveId]) {
          URL.revokeObjectURL(valveImagesMap[valveId]);
        }

        // 2. Remove from local state
        setValveImagesMap((prev) => {
          const newMap = { ...prev };
          delete newMap[valveId];
          return newMap;
        });

        // 3. Invalidate so valves[valveKey].image becomes false
        queryClient.invalidateQueries("Devices");

        // Optionally invalidate the query cache
        queryClient.removeQueries(["ValveImage", device.Device?.id, valveId]);
      },
      onError: (err) => {
        console.error("Failed to delete valve image", err);
        notification.error({ message: tr("Failed to delete image") });
      },
    },
  );

  useEffect(
    () => () =>
      valveIdsWithImages.forEach((valveId) => {
        queryClient.removeQueries(["ValveImage", device.Device?.id, valveId]);
      }),
    [device.Device?.id, queryClient, valveIdsWithImages],
  );

  return (
    <Image.PreviewGroup>
      {manualOpen && (
        <div
          style={{
            padding: 12,
            marginBottom: 12,
            border: "1px solid #f0f0f0",
            borderRadius: 12,
          }}
        >
          <WateringStatus
            valves={valves}
            aplan={liveEnabled ? aplan : null}
            remaining={liveEnabled ? remaining : null}
            commandState={commandState}
            wakeWindowMs={wakeWindowMs}
            sentAt={commandSentAt.current}
            connected={connected}
          />
          <List
            size="small"
            dataSource={Object.keys(valves).slice(0, device.valveCount)}
            renderItem={(valveId) => (
              <List.Item>
                <Row align="middle" justify="space-between" style={{ width: "100%" }}>
                  <Typography.Text>{valves[valveId].name !== `Zone ${valveId}` ? valves[valveId].name : `${tr("Zone")} ${valveId}`}</Typography.Text>

                  <InputNumber
                    disabled={liveWatering || !!commandState}
                    stringMode
                    min={0}
                    max={999}
                    addonAfter={tr("min")}
                    value={String(manualDurations[valveId] ?? "")}
                    onKeyDown={(e) => {
                      // Allow navigation / control keys
                      if (["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(e.key)) {
                        return;
                      }

                      // Block anything that is not a digit
                      if (!/^\d$/.test(e.key)) {
                        e.preventDefault();
                        return;
                      }

                      // Prevent typing values > 999
                      const next = `${e.currentTarget.value}${e.key}`;
                      if (Number(next) > 999) {
                        e.preventDefault();
                      }
                    }}
                    onChange={(value) => {
                      if (value === "" || value == null) {
                        setManualDurations((p) => ({ ...p, [valveId]: 0 }));
                        return;
                      }

                      const num = Math.min(999, Math.max(0, Number(value)));

                      setManualDurations((p) => ({
                        ...p,
                        [valveId]: num,
                      }));
                    }}
                  />
                </Row>
              </List.Item>
            )}
          />
          <Row justify="space-between" align="middle" style={{ marginTop: 12 }}>
            <Typography.Text type="secondary">
              {tr("Total")}: {Object.values(manualDurations).reduce((a, b) => a + b, 0)} {tr("min")}
            </Typography.Text>

            {liveWatering ? (
              <Button
                danger
                type="primary"
                loading={testWatering.isLoading || !!commandState}
                onClick={() => {
                  testWatering.mutate({
                    command: "stop",
                    valve: null,
                    durations: [0, 0, 0, 0, 0, 0, 0, 0],
                  });
                }}
              >
                {tr("Stop Watering")}
              </Button>
            ) : (
              <Button
                type="primary"
                loading={testWatering.isLoading || !!commandState}
                onClick={() => {
                  const hasAnyDuration = Object.values(manualDurations).some((d) => d > 0);

                  if (!hasAnyDuration) {
                    notification.warning({
                      message: tr("Please enter at least one duration"),
                    });
                    return;
                  }

                  testWatering.mutate({
                    command: "startCustom",
                    valve: null,
                    durations: buildDurationsArray(),
                  });
                }}
              >
                {tr("Start Watering")}
              </Button>
            )}
          </Row>
        </div>
      )}

      <List
        bordered
        dataSource={/** @type {(keyof import("../types/global").DeviceValves)[]} */ (Object.keys(valves).slice(0, device.valveCount))}
        renderItem={(valveKey, index) => (
          <List.Item
            style={liveEnabled && aplan?.watering && String(aplan.valve) === String(valveKey)
              ? { background: "rgba(82, 196, 26, 0.08)" }
              : undefined}
            actions={[
              <Switch
                checked={valves[valveKey].state === "on"}
                checkedChildren={tr("On")}
                disabled={isChangingValveState}
                loading={isChangingValveState}
                onChange={(checked) => {
                  if (!device.Device) return;
                  mutateChangeValveState({
                    deviceId: String(device.Device.id),
                    valveId: valveKey,
                    state: checked ? "on" : "off",
                  });
                }}
                unCheckedChildren={tr("Off")}
              />,
              <Radio checked={activeValveId === valveKey} onChange={() => onActiveValveChange(valveKey)} />,
            ]}
            key={`device_${device.deviceSerial}_valve_${index}`}
          >
            <Row align="middle" gutter={12} wrap={false}>
              <Col style={{ lineHeight: 0 }}>
                <div style={{ position: "relative", width: 60, height: 60 }}>
                  <Image
                    fallback="data:image/png;base64,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"
                    height={60}
                    preview={valveImagesMap[valveKey] ? { mask: <EyeFilled style={{ color: "white", fontSize: 24 }} /> } : false}
                    src={valveImagesMap[valveKey] || "placeholder"} // show placeholder if no image
                    width={60}
                    wrapperStyle={{ borderRadius: 12, overflow: "hidden" }}
                  />

                  {/* Only show delete icon if we actually have a valve image */}
                  {!!valveImagesMap[valveKey] && (
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        Modal.confirm({
                          title: tr("Are you sure to delete this image?"),
                          okText: tr("Delete"),
                          cancelText: tr("Cancel"),
                          zIndex: 1060,
                          onOk: () => {
                            deleteValveImage.mutate({ valveId: valveKey.toString() });
                          },
                        });
                      }}
                      style={{
                        position: "absolute",
                        top: 4,
                        right: 4,
                        width: 12,
                        height: 12,
                        borderRadius: "50%",
                        background: "#FF3B30",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                      }}
                    >
                      <CloseOutlined style={{ fontSize: 10, color: "white" }} />
                    </div>
                  )}
                </div>
              </Col>

              <Col span={24} style={{ flex: 1 }}>
                <Typography.Title
                  disabled={isChangingValveName}
                  editable={{
                    tooltip: tr("Edit zone name"),
                    onChange: (value) => {
                      if (!device.Device) return;
                      mutateChangeValveName({
                        deviceId: String(device.Device.id),
                        valveId: valveKey,
                        name: value,
                      });
                    },
                  }}
                  level={5}
                  style={{ marginBlock: 0 }}
                >
                  {valves[valveKey].name !== `Zone ${valveKey}` ? valves[valveKey].name : `${tr("Zone")} ${valveKey}`}
                </Typography.Title>

                {valves[valveKey].name !== `Zone ${valveKey}` && (
                  <Typography.Text type="secondary">
                    {tr("Zone")} {valveKey}
                  </Typography.Text>
                )}
              </Col>
            </Row>
          </List.Item>
        )}
        rowKey={(valveKey) => `device_${device.deviceSerial}_valve_list_${valveKey}`}
        size="small"
        style={{ borderRadius: 12, height, overflowY: "auto" }}
        header={
          <Row justify="space-between" align="middle">
            <Typography.Title level={5} style={{ marginBlock: 0 }}>
              {tr("Irrigation Zones")}
            </Typography.Title>

            <Button type="link" onClick={openManualWatering}>
              {manualOpen ? tr("Hide manual watering") : tr("Manual watering")}
            </Button>
          </Row>
        }
      />
    </Image.PreviewGroup>
  );
};

/**
 * @param {{ device: import("../types/global").Device, onCloseModal?: () => void }} parameters
 * @returns {import("react").JSX.Element | null}
 */
DeviceProvider.Menu = function DeviceProviderMenu({ device, onCloseModal }) {
  const { openDeleteDeviceConfirmModal } = useDevice();
  const [searchParams, setSearchParams] = useSearchParams();
  const isShowingMap = useMemo(() => searchParams.get("show_map") === "true", [searchParams]);
  const isShowingSmartWatering = useMemo(() => searchParams.get("show_smart_watering") === "true", [searchParams]);
  const isShowingAlarmLimit = useMemo(() => searchParams.get("show_alarm_limit") === "true", [searchParams]);

  return (
    <Dropdown
      overlay={
        <Menu>
          {typeof onCloseModal === "function" && (
            <Menu.Item icon={<CloseOutlined />} key="close-modal" onClick={onCloseModal}>
              {tr("Close Device View")}
            </Menu.Item>
          )}

          <Menu.Divider key="divider-1" />

          {!isShowingSmartWatering ? (
            <Menu.Item
              icon={<GiPlantWatering />}
              key="enable-smart-watering"
              onClick={() =>
                setSearchParams((previousSearchParams) => {
                  const newSearchParams = new URLSearchParams(previousSearchParams);
                  newSearchParams.set("show_smart_watering", "true");
                  return newSearchParams;
                })
              }
            >
              {tr("Display Smart Water Meter")}
            </Menu.Item>
          ) : (
            <Menu.Item
              icon={<GiPlantWatering />}
              key="disable-smart-watering"
              onClick={() =>
                setSearchParams((previousSearchParams) => {
                  const newSearchParams = new URLSearchParams(previousSearchParams);
                  newSearchParams.delete("show_smart_watering");
                  return newSearchParams;
                })
              }
            >
              {tr("Close Smart Water Meter")}
            </Menu.Item>
          )}

          {!isShowingMap ? (
            <Menu.Item
              icon={<IoMapOutline />}
              key="show-device-location"
              onClick={() =>
                setSearchParams(
                  (previousSearchParams) => {
                    const newSearchParams = new URLSearchParams(previousSearchParams);
                    newSearchParams.set("show_map", "true");
                    return newSearchParams;
                  },
                  { replace: true },
                )
              }
            >
              {tr("Locate on Map")}
            </Menu.Item>
          ) : (
            <Menu.Item
              icon={<IoMapOutline />}
              key="hide-device-location"
              onClick={() =>
                setSearchParams(
                  (previousSearchParams) => {
                    const newSearchParams = new URLSearchParams(previousSearchParams);
                    newSearchParams.delete("show_map");
                    return newSearchParams;
                  },
                  { replace: true },
                )
              }
            >
              {tr("Close Map")}
            </Menu.Item>
          )}

          <Menu.Divider key="divider-2" />

          <Menu.Item
            danger
            icon={<DeleteOutlined />}
            key="delete-device"
            onClick={() => {
              if (!device.Device?.id) return null;

              openDeleteDeviceConfirmModal(String(device.Device.id));
            }}
          >
            {tr("Delete Device")}
          </Menu.Item>
        </Menu>
      }
      overlayStyle={{ zIndex: 1053 }}
      trigger={["click"]}
    >
      <Button icon={<MoreOutlined />} />
    </Dropdown>
  );
};

/**
 * @param {Object} properties
 * @param {import("../types/global").Device["id"]} properties.deviceId
 * @param {import("../types/global").Device["model"]} properties.deviceModel
 * @param {() => void} [properties.onCloseModal]
 * @returns {import("react").JSX.Element | null}
 */
DeviceProvider.NoDevice = function DeviceProviderNoDevice({ deviceId, deviceModel, onCloseModal }) {
  return (
    <Empty
      description={
        <Space direction="vertical" size={25}>
          <Space direction="vertical" size={0} style={{ width: 300 }}>
            <Typography.Title level={5} style={{ marginBlock: 0 }}>
              {tr("Device is deleted or not found")}
            </Typography.Title>

            <Typography.Text style={{ display: "inline-block", marginBlock: 0, lineHeight: 1.1 }} type="secondary">
              {tr("The device you are looking for might have been removed or does not exist.")}
            </Typography.Text>

            <Typography.Text style={{ display: "inline-block", marginBlock: 0, lineHeight: 1.1 }} type="secondary">
              {tr("Device ID")}: {deviceId}
            </Typography.Text>
          </Space>

          <Button onClick={onCloseModal} type="primary">
            {tr("Close")}
          </Button>
        </Space>
      }
      image={
        <div style={{ position: "relative", overflow: "visible" }}>
          <img alt={deviceId} src={deviceImageMap[deviceModel]} style={{ filter: "grayscale(100%)", height: 48, opacity: 0.6, width: 48 }} />
          <MinusCircleFilled style={{ backgroundColor: "white", borderRadius: "50%", color: "#cc0000", fontSize: 24, position: "absolute", right: -8, top: -8 }} />
        </div>
      }
      imageStyle={{ display: "flex", height: 48, justifyContent: "center" }}
    />
  );
};

/**
 *
 * @param {{ device: import("../types/global").Device; gardenId: import("../types/global").Garden["id"]; isShowing: boolean; style?: import("antd").CardProps["style"] }} parameters
 * @returns {import("react").JSX.Element | null}
 */
DeviceProvider.SmartWaterMeter = function DeviceProviderSmartWaterMeter({ device, gardenId, isShowing = false, style = {} }) {
  const { deviceSerial } = device;
  const queryClient = useQueryClient();

  /** @type {import("react").MutableRefObject<import("antd/lib/select").RefSelectProps | null>} */
  const waterMeterConnectableDevicesSelectRef = useRef(null);

  const locale = localStorage.getItem("locale");

  const tickOptions =
    locale === "en"
      ? [
        { label: `1 ${tr("gallon")}`, value: normalizeTickValue(1 * GALLON_TO_LITRE) },
        { label: `5 ${tr("gallons")}`, value: normalizeTickValue(5 * GALLON_TO_LITRE) },
        { label: `10 ${tr("gallons")}`, value: normalizeTickValue(10 * GALLON_TO_LITRE) },
        { label: `50 ${tr("gallons")}`, value: normalizeTickValue(50 * GALLON_TO_LITRE) },
      ]
      : [
        { label: `1 ${tr("liters")}`, value: 1 },
        { label: `10 ${tr("liters")}`, value: 10 },
        { label: `100 ${tr("liters")}`, value: 100 },
      ];

  const { isLoading: isDeletingWaterMeterConnectedDevices, mutate: mutateDeleteWaterMeterConnectedDevice } = useMutation(
    /** @param {import("../types/global").DeleteWaterMeterConnectedDevicesRequest & {gardenId: import("../types/global").Garden["id"]}} params */
    ({ deviceSerial, connectedDevices, gardenId }) => {
      return axios.delete(`${process.env.REACT_APP_PLANNER_API_END_POINT}/device/water-meter/connected-devices`, {
        data: {
          deviceSerial,
          connectedDevices,
        },
      });
    },
    {
      onSettled: (data, error, vars, ctx) => {
        const { deviceSerial, gardenId } = vars;

        if (gardenId) {
          queryClient.refetchQueries(["Garden", gardenId]);
          queryClient.refetchQueries(["Devices", gardenId]);
        }
        queryClient.refetchQueries(["WaterMeterDetails", deviceSerial]);
      },
    },
  );

  const { isLoading: isUpdatingWaterMeter, mutate: mutateUpdateWaterMeter } = useMutation(
    /** @param {import("../types/global").UpdateWaterMeterRequest} params */
    ({ deviceSerial, status, tickValue, connectedDevices }) => {
      return axios.put(`${process.env.REACT_APP_PLANNER_API_END_POINT}/device/water-meter`, {
        deviceSerial,
        waterMeter: {
          status,
          tickValue,
          connectedDevices,
        },
      });
    },
    {
      onSettled: (data, error, vars, ctx) => {
        queryClient.refetchQueries(["Garden", gardenId]);
        queryClient.refetchQueries(["Devices", gardenId]);
        queryClient.refetchQueries(["WaterMeterDetails", vars.deviceSerial]);
      },
      onSuccess: () => {
        console.log("Successfully deleted connected devices from water meter.");
        if (!waterMeterConnectableDevicesSelectRef.current) return;
        waterMeterConnectableDevicesSelectRef.current.blur();
      },
    },
  );

  /** @type {import("react-query").UseQueryResult<import("../types/global").GetWaterMeterDetailsSuccessResponse, unknown>} */
  const { data: waterMeterDetails } = useQuery(
    ["WaterMeterDetails", deviceSerial],
    () => axios.get(`${process.env.REACT_APP_PLANNER_API_END_POINT}/device/water-meter?deviceSerial=${deviceSerial}`).then((r) => r.data),
    {
      enabled: !!deviceSerial && isShowing,
    },
  );

  const waterMeterConnectedDevices = useMemo(() => {
    if (!waterMeterDetails) return [];

    return waterMeterDetails.data.waterMeter.connectedDevices;
  }, [waterMeterDetails]);

  /** @type {import("react-query").UseQueryResult<import("../types/global").GetDeviceSuccessResponse, unknown>[]} */
  const connectedDeviceDetailsDataArray = useQueries(
    waterMeterConnectedDevices
      .map((cd) => cd.deviceSerial)
      .map((deviceSerial) => ({
        queryKey: ["ConnectedDevice", deviceSerial],
        queryFn: () => axios.get(`${process.env.REACT_APP_PLANNER_API_END_POINT}/device?deviceSerial=${deviceSerial}&mergeConsecutivePlans=false`).then((r) => r.data),
        enabled: !!waterMeterConnectedDevices.length && !!deviceSerial && isShowing,
        staleTime: 5 * 60 * 1000,
        cacheTime: 10 * 60 * 1000,
      })),
  );

  const waterMeterConnectedDeviceDetails = useMemo(
    () => connectedDeviceDetailsDataArray.filter((query) => !!query.data).map((query) => query.data.device),
    [connectedDeviceDetailsDataArray],
  );

  if (!device) return null;
  if (!isShowing) return null;
  if (!waterMeterDetails?.data) return null;

  return (
    <Card
      size="small"
      style={{ ...style, borderRadius: 12, width: "100%" }}
      title={
        <Row align="middle" gutter={10}>
          <Col span={10}>
            <Space>
              <Typography.Title level={5} style={{ marginBlock: 0 }}>
                {tr("Water meter enabled")}
              </Typography.Title>

              <Tooltip title={tr("Toggle to enable or disable smart water meter for this device")}>
                <span>
                  <Switch
                    checked={waterMeterDetails?.data.waterMeter.status === "active"}
                    disabled={isUpdatingWaterMeter}
                    loading={isUpdatingWaterMeter}
                    onChange={(checked) => {
                      mutateUpdateWaterMeter({
                        deviceSerial: device.deviceSerial,
                        status: checked ? "active" : "disabled",
                      });
                    }}
                    size="small"
                  />
                </span>
              </Tooltip>
            </Space>
          </Col>

          <Col span={6}>
            <Select
              disabled={waterMeterDetails?.data.waterMeter.status !== "active" || isUpdatingWaterMeter}
              dropdownStyle={{ zIndex: 1055 }}
              loading={isDeletingWaterMeterConnectedDevices || isUpdatingWaterMeter}
              onChange={(value) => mutateUpdateWaterMeter({ deviceSerial: device.deviceSerial, tickValue: normalizeTickValue(value) })}
              style={{ width: "100%" }}
              value={normalizeTickValue(waterMeterDetails.data.waterMeter.tickValue)}
            >
              {tickOptions.map((opt) => (
                <Select.Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Select.Option>
              ))}
            </Select>
          </Col>

          <Col span={8}>
            <Select
              allowClear
              disabled={waterMeterDetails?.data.waterMeter.status !== "active" || isUpdatingWaterMeter}
              dropdownStyle={{ zIndex: 1056 }}
              filterOption={(inputValue, option) => /** @type {string} */(option?.label ?? "").toLowerCase().includes(inputValue.toLowerCase())}
              loading={isDeletingWaterMeterConnectedDevices || isUpdatingWaterMeter}
              onChange={(/** @type {string} */ v) => !!v && mutateUpdateWaterMeter({ deviceSerial: device.deviceSerial, connectedDevices: [v] })}
              options={waterMeterDetails?.data.connectableDevices.map((d) => ({ label: d.name, value: d._id })) || []}
              optionFilterProp="children"
              placeholder={tr("Connect device")}
              ref={waterMeterConnectableDevicesSelectRef}
              showSearch
              style={{ width: "100%" }}
            />
          </Col>
        </Row>
      }
    >
      {!connectedDeviceDetailsDataArray.length ? (
        <Empty description={tr("No Connected Devices")} image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        waterMeterConnectedDeviceDetails.map((waterMeterConnectedDevice, index) => (
          <Card.Grid
            hoverable={false}
            key={waterMeterConnectedDevice.deviceSerial}
            style={{
              borderBottomLeftRadius: index === 0 ? 12 : 0,
              borderBottomRightRadius: waterMeterConnectedDevices.length === index ? 12 : 0,
              padding: 10,
            }}
          >
            <Row align="middle" gutter={12} justify="space-between" style={{ margin: 0, minHeight: 60, width: "100%" }} wrap={false}>
              <Col>
                {deviceImageMap[waterMeterConnectedDevice.model] && (
                  <Image
                    alt={`${waterMeterConnectedDevice.name} (${waterMeterConnectedDevice.deviceSerial})`}
                    height={24}
                    preview={false}
                    src={deviceImageMap[waterMeterConnectedDevice.model]}
                    width={24}
                  />
                )}
              </Col>

              <Col flex={1}>
                <Typography.Paragraph style={{ lineHeight: 1.1, marginBlock: 0 }}>
                  {waterMeterConnectedDevice.Device?.name || waterMeterConnectedDevice.name || waterMeterConnectedDevice.deviceSerial || waterMeterConnectedDevice}
                </Typography.Paragraph>

                {(!!waterMeterConnectedDevice.Device?.name || !!waterMeterConnectedDevice.name) && (
                  <Typography.Text type="secondary" style={{ fontSize: 12, lineHeight: 1.1 }}>
                    {waterMeterConnectedDevice.deviceSerial || waterMeterConnectedDevice}
                  </Typography.Text>
                )}
              </Col>

              <Col style={{ padding: 0, textAlign: "right" }}>
                <Tooltip title={tr("Disconnect Device")}>
                  <span>
                    <Button
                      danger
                      disabled={isDeletingWaterMeterConnectedDevices || isUpdatingWaterMeter}
                      icon={<DeleteOutlined />}
                      onClick={() => {
                        mutateDeleteWaterMeterConnectedDevice({ deviceSerial: device.deviceSerial, connectedDevices: [waterMeterConnectedDevice._id], gardenId });
                      }}
                      size="small"
                      style={{ border: "none" }}
                    />
                  </span>
                </Tooltip>
              </Col>
            </Row>
          </Card.Grid>
        ))
      )}
    </Card>
  );
};

/**
 * @param {Object} parameters
 * @param {import("../types/global").Device} parameters.device
 * @returns {import("react").JSX.Element | null}
 */
DeviceProvider.Title = function DeviceProviderTitle({ device }) {
  return (
    <Space align="center" size={12}>
      <img alt={device.Device?.name || device.deviceSerial} style={{ border: "none", height: 32, width: 32 }} src={deviceImageMap[device.model]} />

      <Space direction="vertical" size={0}>
        <Typography.Text>{device.Device?.name || device.deviceSerial}</Typography.Text>
        {!!device.Device?.name && (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {device.deviceSerial}
          </Typography.Text>
        )}
      </Space>
    </Space>
  );
};

DeviceProvider.OptimizationSettings = function DeviceProviderOptimizationSettings() {
  const { isOptimizationsPanelOpened, updateIsOptimizationsPanelOpened } = useGarden();

  return (
    <Col md={12} xs={24} style={{ display: "flex", flexDirection: "column" }}>
      <div style={{ position: "relative" }}>
        <Space>
          <Popover
            content={
              <div style={{ minWidth: 300, maxWidth: 360 }}>
                <Row gutter={[0, 12]}>
                  <Col span={24}>
                    <OptimizationProvider.Card optimizationName="rain">
                      <OptimizationProvider.Slider maximum={80} minimum={20} optimizationName="rain" unit="percent" />
                    </OptimizationProvider.Card>
                  </Col>
                  <Col span={24}>
                    <OptimizationProvider.Card optimizationName="sun">
                      <OptimizationProvider.Slider maximum={55} minimum={35} optimizationName="sun" unit="celsius" />
                    </OptimizationProvider.Card>
                  </Col>
                  <Col span={24}>
                    <OptimizationProvider.Card optimizationName="wind">
                      <OptimizationProvider.Slider maximum={100} minimum={10} optimizationName="wind" unit="kilometer-per-hour" />
                    </OptimizationProvider.Card>
                  </Col>
                </Row>
              </div>
            }
            trigger="click"
            placement="bottomRight"
            visible={isOptimizationsPanelOpened}
            onVisibleChange={updateIsOptimizationsPanelOpened}
          >
            {/* 👇 Icons are now the trigger */}
            <Space style={{ cursor: "pointer" }}>
              <OptimizationProvider.Icon optimizationName="rain" />
              <OptimizationProvider.Icon optimizationName="sun" />
              <OptimizationProvider.Icon optimizationName="wind" />
            </Space>
          </Popover>
        </Space>
      </div>
    </Col>
  );
};

DeviceProvider.AlarmLimit = function DeviceProviderAlarmLimit(
  /** @type {{device: import("../types/global").Device, style?: import("antd").CardProps["style"]}} */
  { device, style },
) {
  const locale = localStorage.getItem("locale") || "en";
  const { mutateAsyncSetDeviceAlarm, isSettingDeviceAlarm } = useDevice();
  const [alarmLimitDuration, setAlarmLimitDuration] = useState(
    String(device?.waterMeter.alarm?.alarmEnabled === "active" ? device?.waterMeter.alarm?.durationMinutes : "10"),
  );
  const initialLimitLitres =
    device?.waterMeter?.alarm?.alarmEnabled === "active"
      ? device?.waterMeter?.alarm?.limitLitres
      : locale === "en"
        ? DEFAULT_LIMIT_GALLONS * GALLON_TO_LITRE
        : DEFAULT_LIMIT_LITRES;

  const [alarmLimitLitres, setAlarmLimitLitres] = useState(String(initialLimitLitres));
  const [alarmLimitEnabled, setAlarmLimitEnabled] = useState(device?.waterMeter.alarm?.alarmEnabled);
  const [isUpdated, setIsUpdated] = useState(false);

  const displayLimit = fromLitres(Number(alarmLimitLitres), locale);

  const isUnsaved = useMemo(() => {
    console.count("isUnsaved calculation");

    if (!device) return false;

    const currentAlarmSettings = device.waterMeter.alarm;

    if (!currentAlarmSettings) return false;

    return (
      !isUpdated &&
      (currentAlarmSettings.alarmEnabled !== alarmLimitEnabled ||
        (currentAlarmSettings.alarmEnabled === "active" &&
          (alarmLimitDuration !== String(currentAlarmSettings.durationMinutes) || alarmLimitLitres !== String(currentAlarmSettings.limitLitres))))
    );
  }, [alarmLimitDuration, alarmLimitLitres, device, alarmLimitEnabled, isUpdated]);
  const [isMutated, setIsMutated] = useState(false);

  if (process.env.NODE_ENV === "development") {
    window.device = device;
    window.alarmLimitEnabled = alarmLimitEnabled;
    window.isMutated = isMutated;
    window.isUnsaved = isUnsaved;
  }

  if (!device) return null;

  return (
    <Card
      size="small"
      style={{ ...style, borderRadius: 12, width: "100%" }}
      title={
        <Row align="middle" gutter={10}>
          <Col span={10}>
            <Space>
              <Typography.Title level={5} style={{ marginBlock: 0 }}>
                {tr("Alarm Limit")}
              </Typography.Title>

              <Tooltip title={tr("Toggle to enable or disable smart water meter for this device")}>
                <span>
                  <Switch
                    checked={alarmLimitEnabled === "active"}
                    size="small"
                    onChange={(checked) => {
                      setAlarmLimitEnabled(checked ? "active" : "disabled");
                      setIsUpdated(false);
                      setIsMutated(true);
                    }}
                  />
                </span>
              </Tooltip>
            </Space>
          </Col>

          <Col span={6}>
            <Space>
              <InputNumber
                disabled={alarmLimitEnabled === "disabled"}
                min={"1"}
                style={{ width: 100, borderRadius: 10 }}
                value={alarmLimitDuration}
                onChange={(v) => {
                  setAlarmLimitDuration(v || "");
                  setIsUpdated(false);
                  setIsMutated(true);
                }}
              />
              <Typography.Text>{tr("Minutes")}</Typography.Text>
            </Space>
          </Col>

          <Col span={8}>
            <Space>
              <InputNumber
                disabled={alarmLimitEnabled === "disabled"}
                min={"1"}
                style={{ width: 100, borderRadius: 10 }}
                value={displayLimit}
                onChange={(v) => {
                  const litres = toLitres(Number(v), locale);

                  setAlarmLimitLitres(litres || "");
                  setIsUpdated(false);
                  setIsMutated(true);
                }}
              />
              <Typography.Text>{locale === "en" ? tr("Gallons") : tr("Litre")}</Typography.Text>
            </Space>
          </Col>
        </Row>
      }
    >
      {(isUnsaved || isMutated) && (
        <Alert
          action={
            isUnsaved ? (
              <Button
                loading={isSettingDeviceAlarm}
                type="primary"
                onClick={async () => {
                  await mutateAsyncSetDeviceAlarm({
                    deviceSerial: device.deviceSerial,
                    alarm: {
                      alarmEnabled: alarmLimitEnabled || "disabled",
                      durationMinutes: Number(alarmLimitDuration),
                      limitLitres: Number(alarmLimitLitres),
                    },
                  });

                  setIsUpdated(true);
                }}
              >
                {tr("Save")}
              </Button>
            ) : null
          }
          closable={isUnsaved ? false : true}
          message={
            isUnsaved
              ? alarmLimitEnabled === "active"
                ? tr("You will be notified", {
                  alarmSettingsDurationMinutes: alarmLimitDuration,
                  alarmSettingsLimitLitres: fromLitres(Number(alarmLimitLitres), locale),
                })
                : tr("Alarm limit will be disabled.")
              : tr("Alarm settings saved.")
          }
          showIcon
          type={!isUnsaved ? "success" : "warning"}
        />
      )}
    </Card>
  );
};
